import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Play,
  Save,
  RotateCcw,
  Loader2,
  CheckCircle,
  XCircle,
  Calculator,
  Clock,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Shield,
  Download,
  Zap,
  BookOpen,
  Library,
  Book,
  AlertTriangle,
  FileText,
  List,
  Circle,
  CheckCircle2,
  AlertCircle,
  FileSpreadsheet,
} from "lucide-react";

type SimulateSearch = {
  version?: number;
  preview?: string;
  loadSimulation?: string;
  loadEvent?: string;
  t?: string;
};

export const Route = createFileRoute("/_dashboard/simulators/$id/simulate")({
  component: SimulatePage,
  validateSearch: (search: Record<string, unknown>): SimulateSearch => ({
    version: search.version ? Number(search.version) : undefined,
    preview: search.preview as string | undefined,
    loadSimulation: search.loadSimulation as string | undefined,
    loadEvent: search.loadEvent as string | undefined,
    t: search.t as string | undefined,
  }),
});
import { toast } from "sonner";
import { useForm, Controller } from "react-hook-form";
import { Parser } from "expr-eval";

import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  ClearableSelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { InfoTooltip } from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getSimulator } from "@/lib/api/simulators";
import { listAllFormFieldGroups } from "@/lib/api/form-fields";
import { listAllOutputFieldGroups } from "@/lib/api/output-fields";
import { findReferenceTable, listReferenceTablesForVersion } from "@/lib/api/references";
import { executeSimulation, getDraft, saveDraft, deleteDraft, getSimulation, getSimulationEvent, saveSimulationEvent, getVroomImportAvailability, importVroomFile } from "@/lib/api/simulations";
import { linkAttachments } from "@/lib/api/attachments";
import { createProposalFromSimulation, downloadProposalPptx } from "@/lib/api/proposals";
import { listProposalTemplates } from "@/lib/api/proposal-templates";
import { getErrorMessage } from "@/lib/api/client";
import { formatNumber, formatCurrency, formatDate, parseBBCode, stripBBCode } from "@/lib/utils";
import { useIsAdmin, useIsViewer, useUser } from "@/lib/auth/store";
import { listAgencies } from "@/lib/api/agencies";
import type { FormField, OutputField, SimulationExecuteResponse, FieldCondition, FormFieldGroupWithFields, OutputFieldGroupWithFields, ReferenceTable, ConditionalRule, GroupLayoutOptions, AppearanceOptions, SimulatorSettings, SimulationAttachment, LabelStyle, VroomImportResult } from "@/types";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { AttachmentsSection } from "@/components/simulators/AttachmentsSection";
import { GlossaryModal } from "@/components/simulators/GlossaryModal";
import { ResourcesModal } from "@/components/simulators/ResourcesModal";
import { FormGroupRenderer } from "@/components/simulators/FormGroupRenderer";
import { useIsDarkMode } from "@/lib/theme/ThemeProvider";

// Type for local attachment state
interface LocalAttachment {
  id: number;
  file_name: string;
  file_url: string;
  content_type: string;
  file_size: number;
  title: string | null;
  comment: string | null;
  temp_token: string | null;
  isUploading?: boolean;
}

// Normalize French number formats (e.g. " 386,17" -> 386.17) before parsing
function parseFrenchNumber(value: unknown): number | "" {
  if (typeof value === "number") return value;
  const str = String(value).trim();
  if (!str) return "";
  const cleaned = str.replace(/\s/g, "").replace(",", ".");
  const num = parseFloat(cleaned);
  return isNaN(num) ? "" : num;
}

// Helper function to check if a value is considered "filled"
function isValueFilled(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (value === "") return false;
  if (value === false) return false; // Unchecked checkbox is not "filled"
  if (Array.isArray(value) && (value.length === 0 || value.every(v => v === ""))) return false;
  return true;
}

// Helper function to collect all fields from a group and its children recursively
function collectAllFieldsFromGroupStatic(group: FormFieldGroupWithFields): FormField[] {
  const fields = [...group.fields];
  if (group.children) {
    for (const child of group.children) {
      fields.push(...collectAllFieldsFromGroupStatic(child));
    }
  }
  return fields;
}

// Helper function to generate Tailwind classes from label style
function getLabelClassName(labelStyle?: LabelStyle | null): string {
  if (!labelStyle) return "";
  
  const classes: string[] = [];
  
  if (labelStyle.fontSize) {
    classes.push(`text-${labelStyle.fontSize}`);
  }
  if (labelStyle.fontWeight) {
    classes.push(`font-${labelStyle.fontWeight}`);
  }
  if (labelStyle.fontStyle === "italic") {
    classes.push("italic");
  }
  if (labelStyle.textTransform && labelStyle.textTransform !== "none") {
    classes.push(labelStyle.textTransform);
  }
  if (labelStyle.color && labelStyle.color !== "default" && labelStyle.color !== "custom") {
    const colorMap: Record<string, string> = {
      primary: "text-primary",
      secondary: "text-secondary-foreground",
      muted: "text-muted-foreground",
      destructive: "text-destructive",
    };
    if (colorMap[labelStyle.color]) {
      classes.push(colorMap[labelStyle.color]);
    }
  }
  if (labelStyle.customClassName) {
    classes.push(labelStyle.customClassName);
  }
  
  return classes.join(" ");
}

// Helper function to get inline style for custom color
function getLabelInlineStyle(labelStyle?: LabelStyle | null): React.CSSProperties | undefined {
  if (labelStyle?.color === "custom" && labelStyle.customColor) {
    return { color: labelStyle.customColor };
  }
  return undefined;
}

function SimulatePage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const searchParams = Route.useSearch();
  const simulatorId = Number(id);
  const isAdmin = useIsAdmin();
  const isViewer = useIsViewer();
  const isDarkMode = useIsDarkMode();
  const currentUser = useUser();
  
  // Check for preview mode and version from URL params
  const isPreviewMode = searchParams.preview === "true";
  const urlVersionId = searchParams.version ? Number(searchParams.version) : null;
  const loadSimulationId = searchParams.loadSimulation;
  const loadEventId = searchParams.loadEvent;
  const loadTimestamp = searchParams.t; // Timestamp to force reload
  
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(urlVersionId);
  const [result, setResult] = useState<SimulationExecuteResponse | null>(null);
  // Whether the current calculation result has been explicitly saved as a SimulationEvent
  // (gates the "Enregistrer la cotation" button, which only appears after the simulation has been saved)
  const [simulationEventSaved, setSimulationEventSaved] = useState(false);
  // Store the input values used for the last successful calculation
  // This is used to evaluate output visibility conditions so they don't change when form values change
  const [calculatedInputs, setCalculatedInputs] = useState<Record<string, unknown> | null>(null);
  const [notes, setNotes] = useState("");
  const [tempAttachments, setTempAttachments] = useState<LocalAttachment[]>([]);
  const [showAllOutputs, setShowAllOutputs] = useState(false);
  // Store inline warnings for recommended values per field
  const [fieldWarnings, setFieldWarnings] = useState<Record<string, string | null>>({});
  // Store inline warnings for cross-field validation rules
  const [crossFieldWarnings, setCrossFieldWarnings] = useState<Record<string, string | null>>({});
  // Store inline errors for cross-field blocking rules (severity="error")
  const [crossFieldErrors, setCrossFieldErrors] = useState<Record<string, string | null>>({});
  // Store group validation errors (group slug -> error message)
  const [groupValidationErrors, setGroupValidationErrors] = useState<Record<string, string>>({});
  // Store field validation errors (field slug -> error message)
  const [fieldValidationErrors, setFieldValidationErrors] = useState<Record<string, string>>({});
  // Save simulation dialog state
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [simulationName, setSimulationName] = useState("");
  const [selectedAgencyIds, setSelectedAgencyIds] = useState<number[]>([]);
  const saveDialogRef = useRef<HTMLDivElement>(null);
  const hasManuallyEditedName = useRef(false);
  const [lastCalculatedInputs, setLastCalculatedInputs] = useState<Record<string, unknown> | null>(null);

  const queryClient = useQueryClient();

  const { data: simulator, isLoading: loadingSimulator } = useQuery({
    queryKey: ["simulator", simulatorId],
    queryFn: () => getSimulator(simulatorId),
    enabled: !!simulatorId,
  });

  const { data: agenciesData } = useQuery({
    queryKey: ["agencies-for-user"],
    queryFn: () => listAgencies(),
    staleTime: 5 * 60 * 1000,
  });

  const userAgencies = useMemo(() => {
    if (!agenciesData?.items || !currentUser) return [];
    // Les admins et les utilisateurs de scope national voient toutes les
    // agences (cohérence avec la visibilité globale côté API).
    const isUnrestricted =
      currentUser.role === "admin" || currentUser.scope === "national";
    if (isUnrestricted) return agenciesData.items;
    const userGroupIds = new Set((currentUser.groups ?? []).map((g) => g.id));
    return agenciesData.items.filter((a) => userGroupIds.has(a.group_id));
  }, [agenciesData?.items, currentUser]);

  // In preview mode, show all versions; otherwise only published
  const availableVersions = useMemo(
    () => isPreviewMode 
      ? simulator?.versions || []
      : simulator?.versions.filter((v) => v.status === "published") || [],
    [simulator?.versions, isPreviewMode]
  );

  // Find published versions (for normal mode compatibility)
  const publishedVersions = useMemo(
    () => simulator?.versions.filter((v) => v.status === "published") || [],
    [simulator?.versions]
  );

  // Auto-select version: URL version if specified, otherwise first available
  useEffect(() => {
    if (urlVersionId && simulator?.versions.some(v => v.id === urlVersionId)) {
      setSelectedVersionId(urlVersionId);
    } else if (availableVersions.length > 0 && !selectedVersionId) {
      setSelectedVersionId(availableVersions[0].id);
    }
  }, [availableVersions, selectedVersionId, urlVersionId, simulator?.versions]);

  // Get selected version data for appearance options
  const selectedVersion = useMemo(() => {
    if (!selectedVersionId || !simulator?.versions) return null;
    return simulator.versions.find(v => v.id === selectedVersionId);
  }, [selectedVersionId, simulator?.versions]);

  const { data: formFieldGroups, isLoading: loadingFields } = useQuery({
    queryKey: ["form-field-groups", selectedVersionId],
    queryFn: () => listAllFormFieldGroups(selectedVersionId!),
    enabled: !!selectedVersionId,
  });

  // Flatten all form fields from groups for conditions
  const formFields = useMemo(() => {
    if (!formFieldGroups) return [];
    const flatten = (groups: FormFieldGroupWithFields[]): FormField[] => {
      return groups.flatMap(g => [...g.fields, ...flatten(g.children)]);
    };
    return flatten(formFieldGroups);
  }, [formFieldGroups]);

  // Lookup map: group_id -> group (used by field renderers that need their parent group, e.g. radio layout hints)
  const groupsById = useMemo(() => {
    const map = new Map<number, FormFieldGroupWithFields>();
    if (!formFieldGroups) return map;
    const visit = (groups: FormFieldGroupWithFields[]) => {
      for (const g of groups) {
        map.set(g.id, g);
        visit(g.children);
      }
    };
    visit(formFieldGroups);
    return map;
  }, [formFieldGroups]);

  // Split groups by display context: "form" groups are rendered in the simulation form,
  // "save" groups are rendered in the save cotation dialog
  const formGroups = useMemo(() => {
    if (!formFieldGroups) return [];
    return formFieldGroups.filter(g => g.display_context !== "save");
  }, [formFieldGroups]);

  const saveGroups = useMemo(() => {
    if (!formFieldGroups) return [];
    return formFieldGroups.filter(g => g.display_context === "save");
  }, [formFieldGroups]);

  const saveFieldSlugs = useMemo(() => {
    const slugs = new Set<string>();
    const collect = (groups: FormFieldGroupWithFields[]) => {
      for (const g of groups) {
        for (const f of g.fields) slugs.add(f.slug);
        collect(g.children);
      }
    };
    collect(saveGroups);
    return slugs;
  }, [saveGroups]);

  const { data: outputFieldGroups } = useQuery({
    queryKey: ["output-field-groups", selectedVersionId],
    queryFn: () => listAllOutputFieldGroups(selectedVersionId!),
    enabled: !!selectedVersionId,
  });

  // Flatten output fields and filter only visible ones
  const outputFields = useMemo(() => {
    if (!outputFieldGroups) return [];
    const flatten = (groups: OutputFieldGroupWithFields[]): OutputField[] => {
      return groups.flatMap(g => [...g.fields.filter(f => f.visibility_mode !== 'never'), ...flatten(g.children)]);
    };
    return flatten(outputFieldGroups);
  }, [outputFieldGroups]);

  // Grilles de la version, celles que charge le moteur : les listes y sont résolues par la
  // même règle (voir findReferenceTable), et la liste globale plafonnée à 100 n'entre plus en jeu.
  const { data: referenceTables } = useQuery({
    queryKey: ["version-reference-tables", selectedVersionId, "simulation"],
    queryFn: () => listReferenceTablesForVersion(selectedVersionId!),
    enabled: !!selectedVersionId,
    // Rechargées à chaque ouverture : une grille modifiée juste avant doit s'afficher telle que
    // le moteur la lira, et sa modification n'invalide pas cette clé.
    staleTime: 0,
  });

  // Map display names to stored values for select fields using reference tables.
  // Used by visibility condition evaluation to support conditions saved with display names.
  const displayToValueMap = useMemo(() => {
    if (!referenceTables) return {} as Record<string, Record<string, string>>;
    const map: Record<string, Record<string, string>> = {};
    for (const field of formFields) {
      if (field.field_type !== "select" && field.field_type !== "multiselect" && field.field_type !== "radio") continue;
      const fieldMap: Record<string, string> = {};

      const addFromTable = (tableId: number, displayCol: string, valueCol?: string | null) => {
        const resolvedValueCol = valueCol || displayCol;
        if (resolvedValueCol === displayCol) return;
        const table = findReferenceTable(referenceTables, tableId);
        if (!table?.data) return;
        for (const row of table.data) {
          const label = String(row[displayCol] ?? "");
          const value = String(row[resolvedValueCol] ?? "");
          if (label && value && label !== value) fieldMap[label] = value;
        }
      };

      if (field.reference_table_id && field.reference_column) {
        addFromTable(field.reference_table_id, field.reference_column, field.reference_value_column);
      }
      if (field.conditional_constraints?.rules) {
        for (const rule of field.conditional_constraints.rules) {
          if (rule.reference_table_id && rule.reference_column) {
            addFromTable(rule.reference_table_id, rule.reference_column, rule.reference_value_column);
          }
        }
      }

      if (Object.keys(fieldMap).length > 0) map[field.slug] = fieldMap;
    }
    return map;
  }, [formFields, referenceTables]);

  // Compute default values from all form fields - this ensures collapsed groups have their defaults
  const formDefaultValues = useMemo(() => {
    const defaults: Record<string, unknown> = {};
    formFields.forEach((field) => {
      if (field.default_value !== null && field.default_value !== undefined && field.default_value !== "") {
        // Convert default values based on field type
        if (field.field_type === "checkbox") {
          defaults[field.slug] = field.default_value === true || field.default_value === "true";
        } else if (field.field_type === "number") {
          defaults[field.slug] = parseFrenchNumber(field.default_value);
        } else {
          defaults[field.slug] = field.default_value;
        }
      }
    });
    return defaults;
  }, [formFields]);

  const { control, handleSubmit, reset, watch, trigger, setValue, getValues, formState: { errors, touchedFields, isSubmitted } } = useForm({
    defaultValues: formDefaultValues,
    shouldFocusError: false, // Disabled: we handle scroll ourselves in onInvalid
  });
  const watchedValues = watch();

  // Scroll to a field by its slug
  // Uses a small delay to allow collapsed groups to expand first
  const scrollToField = useCallback((fieldSlug: string) => {
    // Small delay to allow React to re-render (e.g., expand collapsed groups with errors)
    setTimeout(() => {
      const element = document.getElementById(fieldSlug);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus({ preventScroll: true });
      }
    }, 150);
  }, []);

  // Scroll to a group by its slug
  const scrollToGroup = useCallback((groupSlug: string) => {
    setTimeout(() => {
      const element = document.getElementById(`group-${groupSlug}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }, []);

  // Reset form with new defaults when formFields change (e.g., version switch)
  const prevFormDefaultsRef = useRef<string>("");
  useEffect(() => {
    const currentDefaultsStr = JSON.stringify(formDefaultValues);
    if (currentDefaultsStr !== prevFormDefaultsRef.current && Object.keys(formDefaultValues).length > 0) {
      prevFormDefaultsRef.current = currentDefaultsStr;
      reset(formDefaultValues);
    }
  }, [formDefaultValues, reset]);

  // onInvalid is defined lower in the file (right before onSubmit) because it
  // depends on `validateGroupsBeforeSubmit` which is itself defined later.

  // Build a map of source fields -> dependent fields for conditional constraints revalidation
  const conditionalDependencies = useMemo(() => {
    const deps: Record<string, string[]> = {};
    formFields.forEach((field) => {
      if (field.conditional_constraints?.source_field_slug) {
        const source = field.conditional_constraints.source_field_slug;
        if (!deps[source]) {
          deps[source] = [];
        }
        deps[source].push(field.slug);
      }
    });
    return deps;
  }, [formFields]);

  // Track previous values of source fields to detect changes
  const prevSourceValuesRef = useRef<Record<string, unknown>>({});

  // Flag to prevent conditional constraint resets during value restoration (draft/simulation loading)
  const isRestoringRef = useRef(false);

  // Trigger revalidation of dependent fields when source fields change
  // Also reset/adjust fields that have conditional constraints
  useEffect(() => {
    const sourceFields = Object.keys(conditionalDependencies);
    if (sourceFields.length === 0) return;

    const changedSources: string[] = [];
    sourceFields.forEach((sourceSlug) => {
      const currentValue = watchedValues[sourceSlug];
      const prevValue = prevSourceValuesRef.current[sourceSlug];
      if (currentValue !== prevValue) {
        changedSources.push(sourceSlug);
        prevSourceValuesRef.current[sourceSlug] = currentValue;
      }
    });

    if (changedSources.length > 0) {
      // Collect all dependent fields that need revalidation
      const fieldsToRevalidate = changedSources.flatMap(
        (source) => conditionalDependencies[source] || []
      );
      
      if (fieldsToRevalidate.length > 0) {
        fieldsToRevalidate.forEach((fieldSlug) => {
          const field = formFields.find((f) => f.slug === fieldSlug);
          if (!field) return;

          const currentFieldValue = watchedValues[fieldSlug];
          const constraints = field.conditional_constraints;
          if (!constraints) return;

          // Find the matching rule for the current source value
          const sourceValue = watchedValues[constraints.source_field_slug];
          const matchingRule = constraints.rules.find((rule) => {
            if (typeof rule.when_value === "boolean" || typeof sourceValue === "boolean") {
              return rule.when_value === sourceValue;
            }
            return String(rule.when_value) === String(sourceValue);
          });

          // Handle select/multiselect/radio fields
          if (field.field_type === "select" || field.field_type === "multiselect" || field.field_type === "radio") {
            // Check if any rule has a reference_table_id or allowed_values (meaning options can change)
            const hasConditionalOptions = constraints.rules.some(
              (rule) => rule.reference_table_id || (rule.allowed_values && rule.allowed_values.length > 0)
            );
            if (hasConditionalOptions && !isRestoringRef.current) {
              // Reset the field value since the options source changed
              // Skip during restoration to preserve saved values
              setValue(fieldSlug, field.field_type === "multiselect" ? [] : "");
            }
          }
          
          // Handle number fields with conditional min/max constraints
          if (field.field_type === "number" && matchingRule && currentFieldValue !== "" && currentFieldValue !== undefined) {
            const numValue = Number(currentFieldValue);
            if (!isNaN(numValue)) {
              let newValue = numValue;
              let needsReset = false;

              // Check if value exceeds new max
              if (matchingRule.max_value !== undefined && matchingRule.max_value !== null && numValue > matchingRule.max_value) {
                newValue = matchingRule.max_value;
                needsReset = true;
              }
              // Check if value is below new min
              if (matchingRule.min_value !== undefined && matchingRule.min_value !== null && numValue < matchingRule.min_value) {
                newValue = matchingRule.min_value;
                needsReset = true;
              }

              if (needsReset) {
                setValue(fieldSlug, newValue);
              }
            }
          }
        });

        // Trigger revalidation after a short delay to ensure render completes
        setTimeout(() => {
          trigger(fieldsToRevalidate);
        }, 0);
      }
    }
  }, [watchedValues, conditionalDependencies, trigger, formFields, setValue]);

  // Handler for manual group toggle
  const handleToggleGroup = useCallback((groupSlug: string, enabled: boolean) => {
    const toggleSlug = `${groupSlug}_enabled`;
    setValue(toggleSlug, enabled);
  }, [setValue]);

  // Track previously visible fields to detect visibility changes
  const prevVisibleFieldsRef = useRef<Set<string>>(new Set());

  // Reset field values when they become invisible (either by field conditions or group conditions)
  // This ensures hidden fields don't affect calculations
  useEffect(() => {
    if (!formFields.length || !formFieldGroups || !formGroups.length) return;

    // Helper to evaluate a single condition
    const evaluateConditionLocal = (condition: FieldCondition): boolean => {
      const fieldValue = watchedValues[condition.field_slug];
      const fieldMap = displayToValueMap[condition.field_slug];
      const resolvedTarget = (typeof condition.value === "string" && fieldMap?.[condition.value]) || condition.value;

      switch (condition.operator) {
        case "equals":
          return String(fieldValue) === String(resolvedTarget);
        case "not_equals":
          return String(fieldValue) !== String(resolvedTarget);
        case "greater_than":
          return Number(fieldValue) > Number(resolvedTarget);
        case "greater_than_or_equals":
          return Number(fieldValue) >= Number(resolvedTarget);
        case "less_than":
          return Number(fieldValue) < Number(resolvedTarget);
        case "less_than_or_equals":
          return Number(fieldValue) <= Number(resolvedTarget);
        case "contains":
          return String(fieldValue).toLowerCase().includes(String(resolvedTarget).toLowerCase());
        case "not_contains":
          return !String(fieldValue).toLowerCase().includes(String(resolvedTarget).toLowerCase());
        case "is_empty":
          return fieldValue === undefined || fieldValue === null || fieldValue === "";
        case "is_not_empty":
          return fieldValue !== undefined && fieldValue !== null && fieldValue !== "";
        case "in": {
          const inValues = String(resolvedTarget).split(",").map(v => {
            const trimmed = v.trim();
            return (fieldMap?.[trimmed]) || trimmed;
          });
          return inValues.includes(String(fieldValue));
        }
        case "not_in": {
          const notInValues = String(resolvedTarget).split(",").map(v => {
            const trimmed = v.trim();
            return (fieldMap?.[trimmed]) || trimmed;
          });
          return !notInValues.includes(String(fieldValue));
        }
        default:
          return true;
      }
    };

    // Helper to check if a group is visible (considering visibility conditions AND manual toggle)
    const isGroupVisibleLocal = (group: FormFieldGroupWithFields): boolean => {
      // First check visibility conditions
      if (group.visibility_conditions && group.visibility_conditions.conditions.length > 0) {
        const { logic, conditions } = group.visibility_conditions;
        const conditionsMet = logic === "AND"
          ? conditions.every(evaluateConditionLocal)
          : conditions.some(evaluateConditionLocal);
        if (!conditionsMet) return false;
      }
      
      // Then check manual toggle state (if applicable)
      const activation = group.validation_rules?.activation;
      if (activation?.mode === "manual_toggle" && activation?.show_fields_when_inactive === false) {
        const toggleSlug = `${group.slug}_enabled`;
        const isToggleEnabled = watchedValues[toggleSlug] === true;
        if (!isToggleEnabled) return false;
      }
      
      return true;
    };

    // Helper to check if a field is visible (considering both field and all parent group conditions)
    const isFieldVisibleWithGroups = (field: FormField, groups: FormFieldGroupWithFields[]): boolean => {
      // Check basic field visibility
      if (field.visible === false) return false;

      // Find the group containing this field and check all ancestor groups
      // Returns: true = visible, false = not visible, null = field not found in this branch
      const findGroupAndCheckVisibility = (groups: FormFieldGroupWithFields[], parentVisible: boolean): boolean | null => {
        for (const group of groups) {
          // Check if this group is visible (considering parent visibility)
          const groupVisible = parentVisible && isGroupVisibleLocal(group);
          
          // Check if field is in this group
          if (group.fields.some(f => f.slug === field.slug)) {
            if (!groupVisible) return false;
            
            // Group is visible, now check field's own visibility conditions
            if (field.visibility_conditions && field.visibility_conditions.conditions.length > 0) {
              const { logic, conditions } = field.visibility_conditions;
              const fieldConditionsMet = logic === "AND"
                ? conditions.every(evaluateConditionLocal)
                : conditions.some(evaluateConditionLocal);
              return fieldConditionsMet;
            }
            return true;
          }
          
          // Check children groups recursively
          if (group.children.length > 0) {
            const foundInChildren = findGroupAndCheckVisibility(group.children, groupVisible);
            // Only return if field was found (not null)
            if (foundInChildren !== null) return foundInChildren;
          }
        }
        return null; // Field not found in this branch
      };

      const result = findGroupAndCheckVisibility(groups, true);
      // If field not found in any group, assume visible (shouldn't happen normally)
      return result === null ? true : result;
    };

    // Build a set of currently visible field slugs (only form-context fields)
    const currentlyVisibleFields = new Set<string>();
    
    formFields.forEach((field) => {
      if (saveFieldSlugs.has(field.slug)) return;
      if (isFieldVisibleWithGroups(field, formGroups)) {
        currentlyVisibleFields.add(field.slug);
      }
    });

    // Find fields that were visible before but are now hidden
    const fieldsToReset: string[] = [];
    prevVisibleFieldsRef.current.forEach((slug) => {
      if (!currentlyVisibleFields.has(slug)) {
        fieldsToReset.push(slug);
      }
    });

    // Find fields that were hidden but are now visible - restore their default values
    const fieldsToRestoreDefaults: string[] = [];
    currentlyVisibleFields.forEach((slug) => {
      // Only restore if field was not previously visible (or on initial load when prev is empty)
      // and the field currently has an empty value
      if (!prevVisibleFieldsRef.current.has(slug) && prevVisibleFieldsRef.current.size > 0) {
        const currentValue = watchedValues[slug];
        const field = formFields.find((f) => f.slug === slug);
        const hasDefaultValue = field && 
          field.default_value !== null && 
          field.default_value !== undefined && 
          field.default_value !== "";
        
        // Only restore if current value is empty and field has a default value
        const isCurrentlyEmpty = 
          currentValue === undefined || 
          currentValue === null || 
          currentValue === "" ||
          (Array.isArray(currentValue) && currentValue.length === 0);
        
        if (hasDefaultValue && isCurrentlyEmpty) {
          fieldsToRestoreDefaults.push(slug);
        }
      }
    });

    // Also reset hidden fields that have non-empty values (e.g., from formDefaultValues)
    // This is important on initial load when prevVisibleFieldsRef is empty
    // but some hidden fields might have default values that could activate validation groups
    formFields.forEach((field) => {
      if (saveFieldSlugs.has(field.slug)) return;
      if (!currentlyVisibleFields.has(field.slug) && !fieldsToReset.includes(field.slug)) {
        const currentValue = watchedValues[field.slug];
        // Check if the field has a non-empty value that could activate validation
        const hasActivatingValue = 
          currentValue !== undefined && 
          currentValue !== null && 
          currentValue !== "" && 
          currentValue !== false &&
          !(Array.isArray(currentValue) && currentValue.length === 0);
        
        if (hasActivatingValue) {
          fieldsToReset.push(field.slug);
        }
      }
    });

    // Reset values for fields that became hidden or are hidden with non-empty values
    // IMPORTANT: Reset to EMPTY values (not default values) to avoid accidentally
    // activating validation groups. The backend considers 0 as "filled".
    if (fieldsToReset.length > 0) {
      fieldsToReset.forEach((slug) => {
        const field = formFields.find((f) => f.slug === slug);
        if (field) {
          // Reset to empty value based on field type
          if (field.field_type === "checkbox") {
            setValue(slug, false);
          } else if (field.field_type === "multiselect") {
            setValue(slug, []);
          } else if (field.field_type === "number") {
            setValue(slug, "");  // Empty string, not 0 or default value
          } else {
            setValue(slug, "");
          }
        }
      });
    }

    // Restore default values for fields that became visible
    if (fieldsToRestoreDefaults.length > 0) {
      fieldsToRestoreDefaults.forEach((slug) => {
        const field = formFields.find((f) => f.slug === slug);
        if (field && field.default_value !== null && field.default_value !== undefined && field.default_value !== "") {
          // Convert default values based on field type (same logic as formDefaultValues)
          if (field.field_type === "checkbox") {
            setValue(slug, field.default_value === true || field.default_value === "true");
          } else if (field.field_type === "number") {
            setValue(slug, parseFrenchNumber(field.default_value));
          } else {
            setValue(slug, field.default_value);
          }
        }
      });
    }

    // Update the ref with current visible fields
    prevVisibleFieldsRef.current = currentlyVisibleFields;
  }, [watchedValues, formFields, formFieldGroups, formGroups, saveFieldSlugs, setValue, displayToValueMap]);

  const executeMutation = useMutation({
    mutationFn: async (inputs: Record<string, unknown>) => {
      // Execute without saving (preview mode)
      const result = await executeSimulation(selectedVersionId!, {
        inputs,
        save: false,
      });
      
      return result;
    },
    onMutate: () => {
      setResult(null);
      setSimulationEventSaved(false);
    },
    onSuccess: async (data) => {
      if (data.success) {
        setResult(data);
        toast.success("Calcul terminé", {
          description: `Temps d'exécution: ${data.execution_time_ms}ms`,
        });
        // Auto-scroll to results section
        setTimeout(() => {
          const resultsSection = document.getElementById('results-section');
          resultsSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      } else {
        setResult(data);
        toast.error("Erreur de cotation", {
          description: data.errors?.join(", "),
        });
      }
    },
    onError: (error) => {
      toast.error("Erreur", { description: getErrorMessage(error) });
    },
  });

  // Save the current calculation result as a SimulationEvent
  // (explicit "Enregistrer la simulation" action — gates the cotation save button)
  const saveEventMutation = useMutation({
    mutationFn: async () => {
      if (!selectedVersionId || !result || !result.success) {
        throw new Error("Aucun calcul à enregistrer");
      }
      return await saveSimulationEvent(selectedVersionId, {
        inputs: calculatedInputs ?? {},
        outputs: (result.outputs ?? {}) as Record<string, unknown>,
        success: result.success,
        errors: result.errors ?? [],
        warnings: result.warnings ?? [],
        execution_time_ms: result.execution_time_ms ?? 0,
      });
    },
    onSuccess: () => {
      setSimulationEventSaved(true);
      queryClient.invalidateQueries({ queryKey: ["my-simulation-events"] });
      queryClient.invalidateQueries({ queryKey: ["simulation-events"] });
      toast.success("Simulation enregistrée", {
        description: "Vous pouvez maintenant l'enregistrer comme cotation.",
      });
    },
    onError: (error) => {
      toast.error("Erreur", { description: getErrorMessage(error) });
    },
  });

  // Save simulation mutation (with name)
  const saveMutation = useMutation({
    mutationFn: async ({ inputs, name, notesValue, agencyIds }: { inputs: Record<string, unknown>; name: string; notesValue: string; agencyIds: number[] }) => {
      const result = await executeSimulation(selectedVersionId!, {
        inputs,
        name: name || undefined,
        notes: notesValue || undefined,
        save: true,
        agency_ids: agencyIds,
      });
      
      // If simulation was successful and we have temp attachments, link them
      if (result.success && result.simulation_id && tempAttachments.length > 0) {
        const tokens = tempAttachments
          .map(a => a.temp_token)
          .filter((t): t is string => t !== null);
        
        if (tokens.length > 0) {
          try {
            await linkAttachments(result.simulation_id, tokens);
            // Clear temp attachments after linking
            setTempAttachments([]);
          } catch (error) {
            console.error("Failed to link attachments:", error);
            // Don't fail the simulation, just log the error
          }
        }
      }
      
      return result;
    },
    onSuccess: async (data, variables) => {
      if (data.success) {
        setResult(data);
        // Sync calculatedInputs with the inputs actually saved (including the
        // raison sociale chosen in the save dialog) so the proposal PPTX file
        // name and template auto-selection reflect the saved cotation.
        setCalculatedInputs(variables.inputs);
        setShowSaveDialog(false);
        setSimulationName("");
        setSelectedAgencyIds([]);
        setLastCalculatedInputs(null);
        toast.success("Cotation enregistrée", {
          description: data.simulation_ref || `Cotation #${data.simulation_id} sauvegardée`,
        });
        // Clear draft after successful save
        if (selectedVersionId) {
          try {
            await deleteDraft(selectedVersionId);
            setDraftSavedAt(null);
          } catch (error) {
            // Ignore 404 errors (draft doesn't exist)
            console.error("Failed to clear draft:", error);
          }
        }
      } else {
        toast.error("Erreur lors de l'enregistrement", {
          description: data.errors?.join(", "),
        });
      }
    },
    onError: (error) => {
      toast.error("Erreur", { description: getErrorMessage(error) });
    },
  });

  // Generate default simulation name
  const generateDefaultSimulationName = useCallback((raisonSociale?: string) => {
    const now = new Date();
    const date = now.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
    const time = now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    const prefix = raisonSociale?.trim() || "Cotation";
    return `${prefix} du ${date} ${time}`;
  }, []);

  // Handle opening save dialog
  const handleOpenSaveDialog = useCallback(() => {
    hasManuallyEditedName.current = false;
    setSimulationName(generateDefaultSimulationName());
    setLastCalculatedInputs(calculatedInputs);
    setShowSaveDialog(true);
  }, [calculatedInputs, generateDefaultSimulationName]);

  const raisonSociale = watchedValues["raison_sociale"] as string | undefined;
  useEffect(() => {
    if (showSaveDialog && !hasManuallyEditedName.current) {
      setSimulationName(generateDefaultSimulationName(raisonSociale));
    }
  }, [showSaveDialog, raisonSociale, generateDefaultSimulationName]);

  // Handle save simulation - merge save-context field values into the calculated inputs
  const handleSaveSimulation = useCallback(() => {
    if (!lastCalculatedInputs) return;
    const mergedInputs = { ...lastCalculatedInputs };
    for (const slug of saveFieldSlugs) {
      mergedInputs[slug] = watchedValues[slug];
    }
    saveMutation.mutate({
      inputs: mergedInputs,
      name: simulationName,
      notesValue: notes,
      agencyIds: selectedAgencyIds,
    });
  }, [lastCalculatedInputs, saveFieldSlugs, watchedValues, simulationName, notes, selectedAgencyIds, saveMutation]);

  // Validate form before submission
  const validateGroupsBeforeSubmit = useCallback((data: Record<string, unknown>): { valid: boolean; errors: string[]; firstErrorFieldSlug?: string; errorGroupSlugs: Record<string, string>; errorFieldSlugs: Record<string, string> } => {
    if (!formGroups.length) return { valid: true, errors: [], errorGroupSlugs: {}, errorFieldSlugs: {} };
    
    const errors: string[] = [];
    const errorGroupSlugs: Record<string, string> = {};
    const errorFieldSlugs: Record<string, string> = {};
    let firstErrorFieldSlug: string | undefined;
    
    // Helper function to collect all fields from a group and its children recursively
    const collectAllFieldsFromGroup = (group: FormFieldGroupWithFields): FormField[] => {
      const fields = [...group.fields];
      if (group.children) {
        for (const child of group.children) {
          fields.push(...collectAllFieldsFromGroup(child));
        }
      }
      return fields;
    };
    
    // Helper to extract "required" fields from a group's when_active rules
    const getGroupRequiredFields = (group: FormFieldGroupWithFields): string[] => {
      const whenActive = group.validation_rules?.when_active;
      if (!whenActive) return [];
      const requiredFields = whenActive.required_fields ?? [];
      const atLeastOneFields = (whenActive.at_least_one_of ?? []).flatMap(rule => rule.fields);
      return Array.from(new Set([...requiredFields, ...atLeastOneFields]));
    };
    
    // Helper to check if a group is active based on its activation mode
    const checkGroupActive = (group: FormFieldGroupWithFields): boolean => {
      const mode = group.validation_rules?.activation?.mode;
      const keyFields = group.validation_rules?.activation?.key_fields;
      const ignoreDefaultValues = group.validation_rules?.activation?.ignore_default_values;
      
      switch (mode) {
        case "manual_toggle":
          return data[`${group.slug}_enabled`] === true;
        case "auto_key_fields":
          if (!keyFields || keyFields.length === 0) return true;
          return keyFields.every(slug => isValueFilled(data[slug]));
        case "auto_any": {
          // Check all fields including children for auto_any mode
          const allFields = collectAllFieldsFromGroup(group);
          return allFields.some(field => {
            const value = data[field.slug];
            if (!isValueFilled(value)) return false;
            // If ignore_default_values is enabled, check if value differs from default
            if (ignoreDefaultValues && field.default_value !== null && field.default_value !== undefined && field.default_value !== "") {
              return String(value) !== String(field.default_value);
            }
            return true;
          });
        }
        default:
          // No activation rules = check if any field has a value (default behavior)
          if (group.validation_rules?.when_active) {
            const allFields = collectAllFieldsFromGroup(group);
            return allFields.some(field => isValueFilled(data[field.slug]));
          }
          return true;
      }
    };
    
    // Helper to check if a group is "active" based on required fields only
    const isGroupActiveByRequiredFields = (group: FormFieldGroupWithFields): boolean => {
      const requiredFields = getGroupRequiredFields(group);
      if (requiredFields.length === 0) return false;
      return requiredFields.some(slug => isValueFilled(data[slug]));
    };
    
    // Flatten all groups recursively
    const flattenGroups = (groups: FormFieldGroupWithFields[]): FormFieldGroupWithFields[] => {
      return groups.flatMap(g => [g, ...flattenGroups(g.children)]);
    };
    
    const allGroups = flattenGroups(formGroups);
    
    // Helper to evaluate a single condition for validation context
    const evaluateConditionForValidation = (condition: FieldCondition, data: Record<string, unknown>): boolean => {
      const fieldValue = data[condition.field_slug];
      const fieldMap = displayToValueMap[condition.field_slug];
      const resolvedTarget = (typeof condition.value === "string" && fieldMap?.[condition.value]) || condition.value;

      switch (condition.operator) {
        case "equals":
          return String(fieldValue) === String(resolvedTarget);
        case "not_equals":
          return String(fieldValue) !== String(resolvedTarget);
        case "greater_than":
          return Number(fieldValue) > Number(resolvedTarget);
        case "greater_than_or_equals":
          return Number(fieldValue) >= Number(resolvedTarget);
        case "less_than":
          return Number(fieldValue) < Number(resolvedTarget);
        case "less_than_or_equals":
          return Number(fieldValue) <= Number(resolvedTarget);
        case "contains":
          return String(fieldValue).toLowerCase().includes(String(resolvedTarget).toLowerCase());
        case "not_contains":
          return !String(fieldValue).toLowerCase().includes(String(resolvedTarget).toLowerCase());
        case "is_empty":
          return fieldValue === undefined || fieldValue === null || fieldValue === "";
        case "is_not_empty":
          return fieldValue !== undefined && fieldValue !== null && fieldValue !== "";
        case "in": {
          const inValues = String(resolvedTarget).split(",").map(v => {
            const trimmed = v.trim();
            return (fieldMap?.[trimmed]) || trimmed;
          });
          return inValues.includes(String(fieldValue));
        }
        case "not_in": {
          const notInValues = String(resolvedTarget).split(",").map(v => {
            const trimmed = v.trim();
            return (fieldMap?.[trimmed]) || trimmed;
          });
          return !notInValues.includes(String(fieldValue));
        }
        default:
          return true;
      }
    };
    
    // Helper to check if a group is visible for validation purposes
    // This checks both the group's own visibility conditions and its parent's visibility
    const isGroupVisibleForValidation = (
      group: FormFieldGroupWithFields,
      data: Record<string, unknown>
    ): boolean => {
      // Check the group's own visibility conditions
      if (group.visibility_conditions && group.visibility_conditions.conditions.length > 0) {
        const { logic, conditions } = group.visibility_conditions;
        const conditionsMet = logic === "AND"
          ? conditions.every(c => evaluateConditionForValidation(c, data))
          : conditions.some(c => evaluateConditionForValidation(c, data));
        if (!conditionsMet) return false;
      }
      
      // Check manual toggle state (if applicable and fields should be hidden when inactive)
      const activation = group.validation_rules?.activation;
      if (activation?.mode === "manual_toggle" && activation?.show_fields_when_inactive === false) {
        if (data[`${group.slug}_enabled`] !== true) return false;
      }
      
      // Recursively check parent visibility
      if (group.parent_id) {
        const findGroupById = (groups: FormFieldGroupWithFields[], id: number): FormFieldGroupWithFields | null => {
          for (const g of groups) {
            if (g.id === id) return g;
            const found = findGroupById(g.children, id);
            if (found) return found;
          }
          return null;
        };
        const parent = findGroupById(formGroups, group.parent_id);
        if (parent && !isGroupVisibleForValidation(parent, data)) {
          return false;
        }
      }
      
      return true;
    };
    
    // Collect all groups that have validation_rules with when_active
    const collectGroupsWithValidation = (groups: FormFieldGroupWithFields[]): FormFieldGroupWithFields[] => {
      return groups.flatMap(g => {
        const result: FormFieldGroupWithFields[] = [];
        if (g.validation_rules?.when_active) {
          result.push(g);
        }
        result.push(...collectGroupsWithValidation(g.children));
        return result;
      });
    };
    
    // Collect all groups that have manual_toggle mode (for the "at least one enabled" check)
    const collectToggleGroups = (groups: FormFieldGroupWithFields[]): FormFieldGroupWithFields[] => {
      return groups.flatMap(g => {
        const result: FormFieldGroupWithFields[] = [];
        if (g.validation_rules?.activation?.mode === "manual_toggle") {
          result.push(g);
        }
        result.push(...collectToggleGroups(g.children));
        return result;
      });
    };
    
    const toggleGroups = collectToggleGroups(formGroups);
    const groupsWithValidation = collectGroupsWithValidation(formGroups);
    
    // Check global group_validation settings from version settings
    const groupValidationSettings = selectedVersion?.settings?.group_validation;
    if (groupValidationSettings && groupValidationSettings.groups.length > 0) {
      const targetGroups = allGroups.filter(g => groupValidationSettings.groups.includes(g.slug));
      const activeTargetGroups = targetGroups.filter(g => checkGroupActive(g));
      
      switch (groupValidationSettings.mode) {
        case "at_least_one":
          // At least one of the specified groups must be active
          if (activeTargetGroups.length === 0) {
            const message = groupValidationSettings.message || "Veuillez remplir au moins un groupe de champs";
            errors.push(message);
            // Mark all target groups as having errors
            targetGroups.forEach(g => {
              errorGroupSlugs[g.slug] = message;
            });
            return { valid: false, errors, errorGroupSlugs, errorFieldSlugs };
          }
          break;
        case "all_required":
          // All specified groups must be active
          if (activeTargetGroups.length !== targetGroups.length) {
            const inactiveGroups = targetGroups.filter(g => !activeTargetGroups.includes(g));
            const message = groupValidationSettings.message || 
              `Les groupes suivants doivent être remplis: ${inactiveGroups.map(g => g.name).join(", ")}`;
            errors.push(message);
            // Mark inactive groups as having errors
            inactiveGroups.forEach(g => {
              errorGroupSlugs[g.slug] = message;
            });
            return { valid: false, errors, errorGroupSlugs, errorFieldSlugs };
          }
          break;
        // "any" mode = no constraint
      }
    }
    
    // If a parent group is visible and has visible children with when_active rules,
    // require at least one child to be active based on its required fields.
    const parentGroupsWithChildren = allGroups.filter(
      group => group.children.length > 0 && group.validation_rules?.when_active
    );
    for (const parent of parentGroupsWithChildren) {
      if (!isGroupVisibleForValidation(parent, data)) continue;
      
      const visibleChildren = parent.children.filter(
        child => child.validation_rules?.when_active && isGroupVisibleForValidation(child, data)
      );
      
      const eligibleChildren = visibleChildren.filter(
        child => getGroupRequiredFields(child).length > 0
      );
      
      if (eligibleChildren.length === 0) continue;
      
      const anyChildActive = eligibleChildren.some(child => isGroupActiveByRequiredFields(child));
      if (!anyChildActive) {
        const childNames = eligibleChildren.map(child => stripBBCode(child.name)).join(", ");
        const message = `${stripBBCode(parent.name)}: Veuillez renseigner les champs requis dans au moins un des groupes suivants: ${childNames}`;
        errors.push(message);
        // Mark parent with message only (no ring), children with empty string (ring only, no message)
        errorGroupSlugs[parent.slug] = message;
        eligibleChildren.forEach(child => {
          errorGroupSlugs[child.slug] = "__RING_ONLY__"; // Special marker for ring without message
        });
        return { valid: false, errors, errorGroupSlugs, errorFieldSlugs };
      }
    }
    
    // If there are toggle groups, check that at least one is enabled
    // Only consider toggle groups that are actually visible
    if (toggleGroups.length > 0) {
      const visibleToggleGroups = toggleGroups.filter(g => isGroupVisibleForValidation(g, data));
      if (visibleToggleGroups.length > 0) {
        const anyEnabled = visibleToggleGroups.some(g => data[`${g.slug}_enabled`] === true);
        if (!anyEnabled) {
          const message = "Veuillez activer au moins un groupe de champs";
          errors.push(message);
          // Mark all visible toggle groups as having errors
          visibleToggleGroups.forEach(g => {
            errorGroupSlugs[g.slug] = message;
          });
          return { valid: false, errors, errorGroupSlugs, errorFieldSlugs };
        }
      }
    }
    
    // Validate when_active rules for all groups that have them and are active
    for (const group of groupsWithValidation) {
      // Skip validation for groups that are not visible (including parent visibility)
      if (!isGroupVisibleForValidation(group, data)) continue;
      
      // Check if the group is active based on its activation mode
      const groupIsActive = checkGroupActive(group);
      if (!groupIsActive) continue;
      
      const whenActive = group.validation_rules?.when_active;
      if (!whenActive) continue;
      
      // Get all fields from group and children for label lookups
      const allGroupFields = collectAllFieldsFromGroup(group);
      
      // Check required_fields
      if (whenActive.required_fields && whenActive.required_fields.length > 0) {
        for (const fieldSlug of whenActive.required_fields) {
          const field = allGroupFields.find(f => f.slug === fieldSlug);
          // A required field that is hidden by its own visibility_conditions
          // must not block submission (mirrors the backend validate_group_rules).
          // Otherwise a hidden required field — e.g. the "Volume indicatif
          // quotidien" select when "Connaissez-vous votre volume ? = Non" —
          // silently blocks the calculation with an error pinned to an
          // invisible field.
          if (field?.visibility_conditions && field.visibility_conditions.conditions.length > 0) {
            const { logic, conditions } = field.visibility_conditions;
            const fieldVisible = logic === "AND"
              ? conditions.every(c => evaluateConditionForValidation(c, data))
              : conditions.some(c => evaluateConditionForValidation(c, data));
            if (!fieldVisible) continue;
          }
          const value = data[fieldSlug];
          if (!isValueFilled(value)) {
            if (!firstErrorFieldSlug) firstErrorFieldSlug = fieldSlug;
            const fieldLabel = stripBBCode(field?.label || fieldSlug);
            const message = `${stripBBCode(group.name)}: Le champ "${fieldLabel}" est requis`;
            errors.push(message);
            // Mark the group as having errors (ring only, no message at group level)
            if (!errorGroupSlugs[group.slug]) {
              errorGroupSlugs[group.slug] = "__RING_ONLY__";
            }
            // Mark the field with the error message to display below it
            errorFieldSlugs[fieldSlug] = "Ce champ est requis";
          }
        }
      }
      
      // Check at_least_one_of
      if (whenActive.at_least_one_of && whenActive.at_least_one_of.length > 0) {
        for (const constraint of whenActive.at_least_one_of) {
          const isFilledForRule = (fieldSlug: string): boolean => {
            const value = data[fieldSlug];
            if (!isValueFilled(value)) return false;
            // Opt-in: when exclude_zero is set, a numeric 0 does not satisfy the rule
            if (constraint.exclude_zero) {
              const num = parseFrenchNumber(value);
              if (num === 0) return false;
            }
            return true;
          };
          const hasAtLeastOne = constraint.fields.some(isFilledForRule);
          if (!hasAtLeastOne) {
            // Track the first field from this constraint for scrolling
            if (!firstErrorFieldSlug && constraint.fields.length > 0) {
              firstErrorFieldSlug = constraint.fields[0];
            }
            let message: string;
            let fieldMessage: string;
            if (constraint.message) {
              message = `${stripBBCode(group.name)}: ${constraint.message}`;
              fieldMessage = constraint.message;
            } else {
              const fieldLabels = constraint.fields
                .map(slug => stripBBCode(allGroupFields.find(f => f.slug === slug)?.label || slug))
                .join(", ");
              message = `${stripBBCode(group.name)}: Renseignez au moins un champ parmi: ${fieldLabels}`;
              fieldMessage = `Renseignez au moins un de ces champs`;
            }
            errors.push(message);
            // Mark the group as having errors (ring only, no message at group level)
            if (!errorGroupSlugs[group.slug]) {
              errorGroupSlugs[group.slug] = "__RING_ONLY__";
            }
            // Mark all fields in this constraint with the error message
            for (const fieldSlug of constraint.fields) {
              if (!errorFieldSlugs[fieldSlug]) {
                errorFieldSlugs[fieldSlug] = fieldMessage;
              }
            }
          }
        }
      }
      
      // Check conditional_required_fields
      if (whenActive.conditional_required_fields && whenActive.conditional_required_fields.length > 0) {
        for (const rule of whenActive.conditional_required_fields) {
          // Evaluate the condition on the source field
          const conditionMet = evaluateConditionForValidation(
            { field_slug: rule.source_field_slug, operator: rule.operator, value: rule.value ?? "" },
            data
          );
          if (conditionMet) {
            // Check each required field
            for (const fieldSlug of rule.required_fields) {
              const value = data[fieldSlug];
              if (!isValueFilled(value)) {
                if (!firstErrorFieldSlug) firstErrorFieldSlug = fieldSlug;
                const field = allGroupFields.find(f => f.slug === fieldSlug);
                const fieldLabel = stripBBCode(field?.label || fieldSlug);
                const fieldMessage = rule.message || `Le champ "${fieldLabel}" est requis`;
                const message = `${stripBBCode(group.name)}: ${fieldMessage}`;
                errors.push(message);
                // Mark the group as having errors (ring only, no message at group level)
                if (!errorGroupSlugs[group.slug]) {
                  errorGroupSlugs[group.slug] = "__RING_ONLY__";
                }
                // Mark the field with the error message
                if (!errorFieldSlugs[fieldSlug]) {
                  errorFieldSlugs[fieldSlug] = fieldMessage;
                }
              }
            }
          }
        }
      }
    }
    
    // Check field-level conditional_required rules
    // Use only group.fields (direct fields) to avoid processing a field under a parent group
    for (const group of allGroups) {
      if (!isGroupVisibleForValidation(group, data)) continue;
      
      for (const field of group.fields) {
        if (!field.conditional_required) continue;
        if (!field.conditional_required.conditions || field.conditional_required.conditions.length === 0) continue;
        
        // Check if field is visible (field-level visibility)
        if (field.visibility_conditions && field.visibility_conditions.conditions.length > 0) {
          const { logic, conditions } = field.visibility_conditions;
          const conditionsMet = logic === "AND"
            ? conditions.every(c => evaluateConditionForValidation(c, data))
            : conditions.some(c => evaluateConditionForValidation(c, data));
          if (!conditionsMet) continue;
        }
        
        // Evaluate conditional required conditions
        const { logic, conditions } = field.conditional_required;
        const conditionsMet = logic === "AND"
          ? conditions.every(c => evaluateConditionForValidation(c, data))
          : conditions.some(c => evaluateConditionForValidation(c, data));
        
        if (conditionsMet && !isValueFilled(data[field.slug])) {
          if (!firstErrorFieldSlug) firstErrorFieldSlug = field.slug;
          const fieldLabel = stripBBCode(field.label || field.slug);
          const fieldMessage = field.conditional_required.message || `Le champ "${fieldLabel}" est requis`;
          const message = `${stripBBCode(group.name)}: ${fieldMessage}`;
          errors.push(message);
          if (!errorFieldSlugs[field.slug]) {
            errorFieldSlugs[field.slug] = fieldMessage;
          }
        }
      }
    }
    
    return { valid: errors.length === 0, errors, firstErrorFieldSlug, errorGroupSlugs, errorFieldSlugs };
  }, [formGroups, selectedVersion?.settings?.group_validation, displayToValueMap]);

  // Handler called when react-hook-form validation fails - scroll to first error field
  // We find the first error field in DOM order (not object key order) to ensure consistent behavior
  //
  // Important: on top of react-hook-form's native field-level errors (required,
  // validate, min/max), we ALSO run `validateGroupsBeforeSubmit` here so that
  // group-level rules (conditional_required, at_least_one_of, manual toggles,
  // etc.) get surfaced even when a RHF-level required field is empty. Without
  // this, `handleSubmit(onSubmit, onInvalid)` short-circuits to onInvalid as
  // soon as any `required=True` field fails, and the user never sees other
  // errors like `conditional_required` on `zone_geographique` — they stay
  // hidden until every RHF-level required is filled, which is confusing UX.
  const onInvalid = useCallback((formErrors: Record<string, unknown>) => {
    setResult(null);
    setSimulationEventSaved(false);

    const groupValidation = validateGroupsBeforeSubmit(getValues());
    setGroupValidationErrors(groupValidation.errorGroupSlugs);
    setFieldValidationErrors(groupValidation.errorFieldSlugs);

    // Aggregate RHF error slugs + slugs surfaced by group-level validation.
    const errorFieldSlugs = Array.from(new Set([
      ...Object.keys(formErrors),
      ...Object.keys(groupValidation.errorFieldSlugs),
    ]));
    if (errorFieldSlugs.length === 0) return;

    // Get all error elements that exist in the DOM
    const errorElements = errorFieldSlugs
      .map(slug => ({ slug, element: document.getElementById(slug) }))
      .filter((item): item is { slug: string; element: HTMLElement } => item.element !== null);

    if (errorElements.length === 0) return;

    // Sort by document position to find the first one in DOM order
    errorElements.sort((a, b) => {
      const position = a.element.compareDocumentPosition(b.element);
      if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
      return 0;
    });

    // Scroll to the first error field in DOM order
    scrollToField(errorElements[0].slug);
  }, [scrollToField, validateGroupsBeforeSubmit, getValues]);

  const onSubmit = (data: Record<string, unknown>) => {
    setResult(null);
    setSimulationEventSaved(false);

    // Validate groups before submission
    const validation = validateGroupsBeforeSubmit(data);
    if (!validation.valid) {
      // Set group and field validation errors for visual feedback
      setGroupValidationErrors(validation.errorGroupSlugs);
      setFieldValidationErrors(validation.errorFieldSlugs);
      
      // Scroll to the first field or group with an error
      if (validation.firstErrorFieldSlug) {
        scrollToField(validation.firstErrorFieldSlug);
      } else {
        // No specific field, scroll to the first group with an error
        const firstErrorGroupSlug = Object.keys(validation.errorGroupSlugs)[0];
        if (firstErrorGroupSlug) {
          scrollToGroup(firstErrorGroupSlug);
        }
      }
      return;
    }
    
    // Clear group and field validation errors on successful validation
    setGroupValidationErrors({});
    setFieldValidationErrors({});
    
    // Evaluate all cross-field rules (warnings + errors)
    evaluateAllFieldWarnings(data);
    const { hasBlockingErrors, firstErrorField, firstErrorGroup } = evaluateAllCrossFieldWarnings(data);
    
    if (hasBlockingErrors) {
      if (firstErrorField) {
        scrollToField(firstErrorField);
      } else if (firstErrorGroup) {
        scrollToGroup(firstErrorGroup);
      }
      return;
    }
    
    // Store the inputs used for this calculation - this will be used for output visibility
    setCalculatedInputs({ ...data });
    executeMutation.mutate(data);
  };

  const handleReset = () => {
    reset();
    setResult(null);
    setSimulationEventSaved(false);
    setCalculatedInputs(null);
    setNotes("");
    setTempAttachments([]);
    setFieldWarnings({});
    setCrossFieldWarnings({});
    setCrossFieldErrors({});
    setGroupValidationErrors({});
    setFieldValidationErrors({});
    setShowSaveDialog(false);
    setSimulationName("");
    setLastCalculatedInputs(null);
    clearDraft();
  };

  // Get appearance options with defaults
  const appearanceOptions: AppearanceOptions = selectedVersion?.appearance_options || {};
  const formGroupsPerRow = appearanceOptions.form_groups_per_row ?? 1;
  const outputGroupsPerRow = appearanceOptions.output_groups_per_row ?? 1;
  const resultsPosition = appearanceOptions.results_position ?? "right";
  const resultsWidth = appearanceOptions.results_width ?? "half";
  const attachmentsPosition = appearanceOptions.attachments_position ?? "bottom";
  const showOutputDescriptions = appearanceOptions.show_output_descriptions ?? true;
  const showSpinbox = appearanceOptions.show_spinbox ?? true;

  // Get settings with defaults
  const simulatorSettings: SimulatorSettings = selectedVersion?.settings || {};
  const autoCalculate = simulatorSettings.auto_calculate ?? false;

  // Les résultats affichés — valeurs comme lignes visibles — sont ceux du
  // dernier calcul. Modifier une saisie sans relancer laisse donc une grille
  // qui ne correspond plus au formulaire : une zone conditionnée à une option
  // de livraison peut y manquer alors que l'option vient d'être élargie.
  const resultatPerime = useMemo(() => {
    if (!result?.success || !calculatedInputs) return false;
    const memeValeur = (a: unknown, b: unknown) => {
      const vide = (v: unknown) => v === undefined || v === null || v === "";
      if (vide(a) && vide(b)) return true;
      if (typeof a === "number" || typeof b === "number") {
        return Number(a) === Number(b);
      }
      return String(a) === String(b);
    };
    const slugs = new Set([...Object.keys(calculatedInputs), ...Object.keys(watchedValues)]);
    return [...slugs].some((slug) => !memeValeur(calculatedInputs[slug], watchedValues[slug]));
  }, [result, calculatedInputs, watchedValues]);
  const autoSaveDraft = simulatorSettings.auto_save_draft ?? true;  // Default to true
  const enableAttachments = simulatorSettings.enable_attachments ?? false;
  const maxAttachments = simulatorSettings.max_attachments ?? 0;
  const allowedFileTypes = simulatorSettings.allowed_file_types ?? [];
  const enableNotes = simulatorSettings.enable_notes ?? true;  // Default to true for backward compatibility
  
  // Output display settings
  const hideZeroOutputs = simulatorSettings.hide_zero_outputs ?? false;
  const showAllOutputsButton = simulatorSettings.show_all_outputs_button ?? false;
  
  // Documentation features settings
  const enableForeword = simulatorSettings.enable_foreword ?? false;
  const forewordTitle = simulatorSettings.foreword_title || "Avant-propos";
  const forewordContent = simulatorSettings.foreword_content || "";
  const enableResources = simulatorSettings.enable_resources ?? false;
  const resourcesTitle = simulatorSettings.resources_title || "Ressources";
  const resources = simulatorSettings.resources ?? [];
  const enableGlossary = simulatorSettings.enable_glossary ?? false;
  const glossaryTitle = simulatorSettings.glossary_title || "Glossaire";
  const glossaryTerms = simulatorSettings.glossary_terms ?? [];
  
  // Cross-field rules: include inline warnings AND all error rules (errors are always inline for blocking UX).
  // Les règles `display_mode === "disable_target"` sont exclues de l'évaluation inline :
  // elles pilotent un `disabled` + tooltip sur les checkboxes listées dans `target_fields`
  // (cf. `disabledCheckboxMap` plus bas). Le backend continue de les évaluer sur submit.
  const allCrossFieldRules = simulatorSettings.cross_field_warnings ?? [];
  const crossFieldWarningRules = allCrossFieldRules.filter(
    rule =>
      rule.display_mode !== "disable_target" &&
      (rule.display_mode !== "results_only" || rule.severity === "error")
  );

  // Map: target field slug -> inline color style for NON-blocking warnings.
  // Lets a warning-severity rule opt into red styling via `inline_style: "error"`
  // (alerts strongly without blocking). Errors are handled by crossFieldErrors.
  const crossFieldWarningStyles = useMemo<Record<string, "warning" | "error">>(() => {
    const map: Record<string, "warning" | "error"> = {};
    for (const rule of crossFieldWarningRules) {
      if (rule.severity === "error") continue;
      if (rule.inline_style !== "error") continue;
      for (const slug of rule.target_fields ?? []) {
        map[slug] = "error";
      }
    }
    return map;
  }, [crossFieldWarningRules]);
  
  // Glossary modal state
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  
  // Resources modal state
  const [resourcesOpen, setResourcesOpen] = useState(false);

  // Draft save state
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);
  const [isDraftLoaded, setIsDraftLoaded] = useState(false);
  const draftSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-calculate debounce timer ref
  const autoCalcTimerRef = useRef<NodeJS.Timeout | null>(null);
  const prevWatchedValuesRef = useRef<string>("");

  // Check if a value is outside the recommended range and return the warning message
  const checkRecommendedValue = useCallback((field: FormField, value: unknown): string | null => {
    if (field.field_type !== "number") return null;
    if (value === "" || value === undefined || value === null) return null;
    
    const numValue = typeof value === "number" ? value : parseFloat(String(value));
    if (isNaN(numValue)) return null;
    
    const recMin = field.recommended_min_value;
    const recMax = field.recommended_max_value;
    
    // No recommended values defined
    if (recMin === null && recMax === null) return null;
    
    let isOutsideRange = false;
    if (recMin !== null && recMax !== null) {
      isOutsideRange = numValue < recMin || numValue > recMax;
    } else if (recMin !== null) {
      isOutsideRange = numValue < recMin;
    } else if (recMax !== null) {
      isOutsideRange = numValue > recMax;
    }
    
    if (!isOutsideRange) return null;
    
    // Return custom message or generate default
    if (field.recommended_warning_message) {
      return field.recommended_warning_message;
    }
    
    // Generate default message
    if (recMin !== null && recMax !== null) {
      return `Attention: ${recMin} à ${recMax} préconisé pour '${stripBBCode(field.label)}'`;
    } else if (recMin !== null) {
      return `Attention: minimum ${recMin} préconisé pour '${stripBBCode(field.label)}'`;
    } else {
      return `Attention: maximum ${recMax} préconisé pour '${stripBBCode(field.label)}'`;
    }
  }, []);

  // Evaluate ALL single-field recommended value warnings (called on form submission and auto-calculate)
  // This ensures warnings are shown even if the user didn't blur each field
  const evaluateAllFieldWarnings = useCallback((values: Record<string, unknown>) => {
    if (!formFields || formFields.length === 0) return;
    
    const newWarnings: Record<string, string | null> = {};
    
    for (const field of formFields) {
      if (field.field_type === "number") {
        const value = values[field.slug];
        const warning = checkRecommendedValue(field, value);
        newWarnings[field.slug] = warning;
      }
    }
    
    setFieldWarnings(prev => ({ ...prev, ...newWarnings }));
  }, [formFields, checkRecommendedValue]);

  // Build a normalized evaluation context from form values.
  // Pre-populates ALL known form field slugs with 0 so expr-eval never throws on missing variables.
  const buildEvalContext = useCallback((values: Record<string, unknown>): Record<string, number | string> => {
    const context: Record<string, number | string> = {};
    for (const field of formFields) {
      context[field.slug] = 0;
    }
    for (const [key, value] of Object.entries(values)) {
      if (value === "" || value === undefined || value === null) {
        context[key] = 0;
      } else if (typeof value === "number") {
        context[key] = value;
      } else if (typeof value === "string" && !isNaN(parseFloat(value))) {
        context[key] = parseFloat(value);
      } else if (typeof value === "boolean") {
        context[key] = value ? 1 : 0;
      } else {
        context[key] = value as string;
      }
    }
    return context;
  }, [formFields]);

  // Disabled-target cross-field rules: for each checkbox listed in `target_fields`
  // that is currently FALSY, if toggling it to true would make the rule's condition
  // evaluate to true, we disable that checkbox and attach `rule.message` as tooltip.
  // Cela prévient l'état invalide côté UI au lieu d'afficher une erreur rouge a posteriori.
  // Ne touche jamais une case déjà cochée (sinon l'utilisateur ne pourrait plus la décocher).
  const disabledCheckboxMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    const disableRules = allCrossFieldRules.filter(
      (r) => r.display_mode === "disable_target" && r.enabled !== false
    );
    if (disableRules.length === 0) return map;

    const parser = new Parser();
    const baseContext = buildEvalContext(watchedValues);

    for (const rule of disableRules) {
      let expr;
      try {
        expr = parser.parse(rule.condition);
      } catch (error) {
        console.error(`[DisableTarget] Invalid condition: ${rule.condition}`, error);
        continue;
      }
      for (const slug of rule.target_fields ?? []) {
        const current = baseContext[slug];
        // Skip if already truthy — the user must be able to uncheck.
        if (current && current !== 0 && current !== "0") continue;
        // What-if: simulate checking this toggle (numeric 1, matches our bool→1/0 coercion).
        const ctx = { ...baseContext, [slug]: 1 };
        try {
          if (expr.evaluate(ctx)) {
            map[slug] = rule.message;
          }
        } catch (error) {
          console.error(`[DisableTarget] Error evaluating ${rule.condition} with ${slug}=1`, error);
        }
      }
    }
    return map;
  }, [allCrossFieldRules, watchedValues, buildEvalContext]);

  // Evaluate ALL cross-field warning rules (called on form submission and auto-calculate)
  // Evaluates all inline cross-field rules (warnings + errors).
  // Returns { hasBlockingErrors, firstErrorField } for scroll-to-error support.
  const evaluateAllCrossFieldWarnings = useCallback((values: Record<string, unknown>): { hasBlockingErrors: boolean; firstErrorField: string | null; firstErrorGroup: string | null } => {
    if (!crossFieldWarningRules || crossFieldWarningRules.length === 0) return { hasBlockingErrors: false, firstErrorField: null, firstErrorGroup: null };
    
    const newWarnings: Record<string, string | null> = {};
    const newErrors: Record<string, string | null> = {};
    const newGroupErrors: Record<string, string> = {};
    const crossFieldGroupSlugs = new Set<string>();
    let hasBlockingErrors = false;
    let firstErrorField: string | null = null;
    let firstErrorGroup: string | null = null;
    const parser = new Parser();
    const context = buildEvalContext(values);
    
    for (const rule of crossFieldWarningRules) {
      if (rule.enabled === false) continue;
      
      for (const gs of rule.target_groups ?? []) {
        crossFieldGroupSlugs.add(gs);
      }
      
      const isError = rule.severity === "error";
      const targetMap = isError ? newErrors : newWarnings;
      
      try {
        const expr = parser.parse(rule.condition);
        const result = expr.evaluate(context);
        
        if (result && isError) {
          hasBlockingErrors = true;
          if (!firstErrorField && rule.target_fields?.length) {
            firstErrorField = rule.target_fields[0];
          }
          if (!firstErrorGroup && rule.target_groups?.length) {
            firstErrorGroup = rule.target_groups[0];
          }
        }
        
        for (const targetField of rule.target_fields || []) {
          if (result) {
            targetMap[targetField] = rule.message;
          } else if (!(targetField in targetMap)) {
            targetMap[targetField] = null;
          }
        }
        
        if (result) {
          for (const gs of rule.target_groups ?? []) {
            newGroupErrors[gs] = rule.message;
          }
        }
      } catch (error) {
        console.error(`[CrossFieldWarnings] Error evaluating rule: ${rule.condition}`, error);
        for (const targetField of rule.target_fields || []) {
          if (!(targetField in targetMap)) {
            targetMap[targetField] = null;
          }
        }
      }
    }
    
    setCrossFieldWarnings(prev => ({ ...prev, ...newWarnings }));
    setCrossFieldErrors(prev => ({ ...prev, ...newErrors }));
    if (crossFieldGroupSlugs.size > 0) {
      setGroupValidationErrors(prev => {
        const updated = { ...prev };
        for (const gs of crossFieldGroupSlugs) delete updated[gs];
        return { ...updated, ...newGroupErrors };
      });
    }
    return { hasBlockingErrors, firstErrorField, firstErrorGroup };
  }, [crossFieldWarningRules, buildEvalContext]);

  // Auto-calculate effect - triggers calculation when inputs change (if enabled)
  // IMPORTANT: This effect should ONLY run when auto_calculate setting is explicitly true
  useEffect(() => {
    // FIRST: Check if auto-calculate is strictly enabled (must be boolean true)
    // If not enabled, do nothing at all - don't even track values
    const isAutoCalcEnabled = autoCalculate === true;
    if (!isAutoCalcEnabled) {
      // Clear any pending timer when auto-calculate is disabled
      if (autoCalcTimerRef.current) {
        clearTimeout(autoCalcTimerRef.current);
        autoCalcTimerRef.current = null;
      }
      return;
    }

    // Auto-calculate is enabled, proceed with the logic
    if (!selectedVersionId || loadingFields) return;

    // Serialize current values to detect real changes
    const currentValuesStr = JSON.stringify(watchedValues);
    
    // Skip if values haven't actually changed (prevent initial/duplicate triggers)
    const valuesChanged = currentValuesStr !== prevWatchedValuesRef.current;
    prevWatchedValuesRef.current = currentValuesStr;
    
    if (!valuesChanged) return;

    // Skip if no values have been entered yet
    const hasAnyValue = Object.values(watchedValues).some(
      (v) => v !== undefined && v !== null && v !== ""
    );
    if (!hasAnyValue) return;
    
    // Validate groups before auto-calculation
    const validation = validateGroupsBeforeSubmit(watchedValues);
    if (!validation.valid) return;

    // Clear any existing timer
    if (autoCalcTimerRef.current) {
      clearTimeout(autoCalcTimerRef.current);
    }

    // Set debounced calculation
    autoCalcTimerRef.current = setTimeout(() => {
      // Evaluate all warnings before auto-calculation
      evaluateAllFieldWarnings(watchedValues);
      const { hasBlockingErrors, firstErrorField, firstErrorGroup } = evaluateAllCrossFieldWarnings(watchedValues);
      if (hasBlockingErrors) {
        if (firstErrorField) {
          scrollToField(firstErrorField);
        } else if (firstErrorGroup) {
          scrollToGroup(firstErrorGroup);
        }
        return;
      }
      // Store the inputs used for this calculation - this will be used for output visibility
      setCalculatedInputs({ ...watchedValues });
      executeMutation.mutate(watchedValues);
    }, 500);

    return () => {
      if (autoCalcTimerRef.current) {
        clearTimeout(autoCalcTimerRef.current);
      }
    };
  }, [watchedValues, autoCalculate, selectedVersionId, loadingFields, validateGroupsBeforeSubmit, evaluateAllFieldWarnings, evaluateAllCrossFieldWarnings]);

  // Load draft from API on mount (only in manual mode with auto_save_draft enabled)
  // Skip if loading from simulation history (loadSimulationId or loadEventId is present)
  useEffect(() => {
    if (autoCalculate || !autoSaveDraft || !selectedVersionId || loadingFields || isDraftLoaded || loadSimulationId || loadEventId) return;

    const loadDraft = async () => {
      try {
        const draft = await getDraft(selectedVersionId);
        if (draft && draft.inputs && typeof draft.inputs === "object") {
          // Restore form values (flag prevents conditional constraint resets from clearing dependent fields)
          isRestoringRef.current = true;
          Object.entries(draft.inputs).forEach(([key, value]) => {
            setValue(key, value);
          });
          setTimeout(() => { isRestoringRef.current = false; }, 0);
          // Restore notes if available
          if (draft.notes) {
            setNotes(draft.notes);
          }
          // Restore calculation results if available
          if (draft.outputs && Object.keys(draft.outputs).length > 0) {
            setResult({
              success: true,
              outputs: draft.outputs,
              execution_time_ms: draft.execution_time_ms || 0,
              errors: [],
              warnings: [],
            });
            // Set calculated inputs for visibility conditions
            setCalculatedInputs(draft.inputs);
          }
          setDraftSavedAt(draft.updated_at || null);
        }
      } catch (error) {
        console.error("Failed to load draft:", error);
      } finally {
        setIsDraftLoaded(true);
      }
    };

    loadDraft();
  }, [selectedVersionId, loadingFields, autoCalculate, autoSaveDraft, isDraftLoaded, loadSimulationId, loadEventId, setValue]);

  // Evaluate a single condition against form values
  const evaluateCondition = useCallback((condition: FieldCondition, values: Record<string, unknown>): boolean => {
    const fieldValue = values[condition.field_slug];
    const fieldMap = displayToValueMap[condition.field_slug];
    const resolvedTarget = (typeof condition.value === "string" && fieldMap?.[condition.value]) || condition.value;

    switch (condition.operator) {
      case "equals":
        return String(fieldValue) === String(resolvedTarget);
      case "not_equals":
        return String(fieldValue) !== String(resolvedTarget);
      case "greater_than":
        return Number(fieldValue) > Number(resolvedTarget);
      case "greater_than_or_equals":
        return Number(fieldValue) >= Number(resolvedTarget);
      case "less_than":
        return Number(fieldValue) < Number(resolvedTarget);
      case "less_than_or_equals":
        return Number(fieldValue) <= Number(resolvedTarget);
      case "contains":
        return String(fieldValue).toLowerCase().includes(String(resolvedTarget).toLowerCase());
      case "not_contains":
        return !String(fieldValue).toLowerCase().includes(String(resolvedTarget).toLowerCase());
      case "is_empty":
        return fieldValue === undefined || fieldValue === null || fieldValue === "";
      case "is_not_empty":
        return fieldValue !== undefined && fieldValue !== null && fieldValue !== "";
      case "in": {
        const inValues = String(resolvedTarget).split(",").map(v => {
          const trimmed = v.trim();
          return (fieldMap?.[trimmed]) || trimmed;
        });
        return inValues.includes(String(fieldValue));
      }
      case "not_in": {
        const notInValues = String(resolvedTarget).split(",").map(v => {
          const trimmed = v.trim();
          return (fieldMap?.[trimmed]) || trimmed;
        });
        return !notInValues.includes(String(fieldValue));
      }
      default:
        return true;
    }
  }, [displayToValueMap]);

  // Locate the immediate `FormFieldGroup` that owns a given field slug.
  // Hoisted at the component level so it can be reused both inside
  // `findMissingRequiredFieldsForInputs` (visibility evaluation) and from
  // the historical reopen `useEffect`s (inline error merging).
  const findOwningGroup = useCallback(
    (
      groups: FormFieldGroupWithFields[],
      slug: string
    ): FormFieldGroupWithFields | null => {
      for (const g of groups) {
        if (g.fields.some((f) => f.slug === slug)) return g;
        const found = findOwningGroup(g.children, slug);
        if (found) return found;
      }
      return null;
    },
    []
  );

  // Scan the form for `required=true` fields currently visible (given the
  // supplied input snapshot) whose value is missing. Mirrors backend
  // `execute.py::validate_inputs` semantics (L757–767) so we can mark these
  // fields inline when reopening a historical saved simulation/cotation.
  const findMissingRequiredFieldsForInputs = useCallback(
    (inputs: Record<string, unknown>): FormField[] => {
      if (!formFields.length || !formGroups.length) return [];

      const findGroupById = (
        groups: FormFieldGroupWithFields[],
        id: number
      ): FormFieldGroupWithFields | null => {
        for (const g of groups) {
          if (g.id === id) return g;
          const found = findGroupById(g.children, id);
          if (found) return found;
        }
        return null;
      };

      const isGroupVisibleFor = (
        group: FormFieldGroupWithFields,
        data: Record<string, unknown>
      ): boolean => {
        if (
          group.visibility_conditions &&
          group.visibility_conditions.conditions.length > 0
        ) {
          const { logic, conditions } = group.visibility_conditions;
          const met = logic === "AND"
            ? conditions.every((c) => evaluateCondition(c, data))
            : conditions.some((c) => evaluateCondition(c, data));
          if (!met) return false;
        }
        if (group.parent_id) {
          const parent = findGroupById(formGroups, group.parent_id);
          if (parent && !isGroupVisibleFor(parent, data)) return false;
        }
        return true;
      };

      const isFieldVisibleFor = (
        field: FormField,
        data: Record<string, unknown>
      ): boolean => {
        if (field.visible === false) return false;
        const owning = findOwningGroup(formGroups, field.slug);
        if (owning && !isGroupVisibleFor(owning, data)) return false;
        if (
          field.visibility_conditions &&
          field.visibility_conditions.conditions.length > 0
        ) {
          const { logic, conditions } = field.visibility_conditions;
          return logic === "AND"
            ? conditions.every((c) => evaluateCondition(c, data))
            : conditions.some((c) => evaluateCondition(c, data));
        }
        return true;
      };

      const isMissing = (value: unknown): boolean => {
        if (value === undefined || value === null) return true;
        if (value === "") return true;
        if (Array.isArray(value) && value.length === 0) return true;
        return false;
      };

      return formFields.filter(
        (f) =>
          f.required === true &&
          f.visible !== false &&
          isFieldVisibleFor(f, inputs) &&
          isMissing(inputs[f.slug])
      );
    },
    [formFields, formGroups, evaluateCondition, findOwningGroup]
  );

  // ============== Import VROOM (OPTIMISS) ==============
  // Un export VROOM est lu côté API puis récapitulé dans une fenêtre : rien ne
  // touche au formulaire tant que l'utilisateur n'a pas accepté l'import. Il
  // s'applique alors comme la réouverture d'une cotation : injection sous
  // `isRestoringRef`, puis les champs obligatoires que le fichier ne renseigne
  // pas (repas, véhicule) ressortent en rouge. Aucun calcul n'est lancé.
  const vroomInputRef = useRef<HTMLInputElement>(null);
  const [vroomPreview, setVroomPreview] = useState<{
    fileName: string;
    result: VroomImportResult;
    replacesInput: boolean;
  } | null>(null);

  const { data: vroomImport } = useQuery({
    queryKey: ["vroom-import", selectedVersionId],
    queryFn: () => getVroomImportAvailability(selectedVersionId!),
    enabled: !!selectedVersionId,
    staleTime: Infinity,
  });

  // Une saisie qui s'écarte des valeurs par défaut serait perdue : le
  // récapitulatif le signale avant que l'utilisateur n'accepte.
  const hasFormInput = () =>
    result !== null ||
    Object.entries(getValues()).some(([slug, value]) => {
      const isEmpty =
        value === undefined ||
        value === null ||
        value === "" ||
        value === false ||
        (Array.isArray(value) && value.length === 0);
      return !isEmpty && value !== formDefaultValues[slug];
    });

  const applyVroomImport = (importResult: VroomImportResult) => {
    handleReset();
    setTimeout(() => {
      isRestoringRef.current = true;
      Object.entries(importResult.inputs).forEach(([slug, value]) => {
        setValue(slug, value);
      });
      setTimeout(() => { isRestoringRef.current = false; }, 0);

      setTimeout(() => {
        const currentValues = getValues();
        evaluateAllFieldWarnings(currentValues);
        evaluateAllCrossFieldWarnings(currentValues);
        const groupValidation = validateGroupsBeforeSubmit(currentValues);
        const missingFieldRequired = findMissingRequiredFieldsForInputs(currentValues);

        const mergedFieldErrors: Record<string, string> = { ...groupValidation.errorFieldSlugs };
        const mergedGroupErrors: Record<string, string> = { ...groupValidation.errorGroupSlugs };
        for (const f of missingFieldRequired) {
          if (!mergedFieldErrors[f.slug]) {
            mergedFieldErrors[f.slug] = "Ce champ est requis";
          }
          const owning = findOwningGroup(formGroups, f.slug);
          if (owning && !mergedGroupErrors[owning.slug]) {
            mergedGroupErrors[owning.slug] = "__RING_ONLY__";
          }
        }
        setFieldValidationErrors(mergedFieldErrors);
        setGroupValidationErrors(mergedGroupErrors);
      }, 200);
    }, 100);
  };

  const vroomImportMutation = useMutation({
    mutationFn: (file: File) => importVroomFile(selectedVersionId!, file),
    onSuccess: (importResult, file) => {
      setVroomPreview({ fileName: file.name, result: importResult, replacesInput: hasFormInput() });
    },
    onError: (error) => {
      toast.error("Import VROOM impossible", { description: getErrorMessage(error) });
    },
  });

  const handleVroomFile = (input: HTMLInputElement) => {
    const file = input.files?.[0];
    // Vidé pour qu'un même fichier puisse être réimporté.
    input.value = "";
    if (file) vroomImportMutation.mutate(file);
  };

  const acceptVroomImport = () => {
    if (vroomPreview) applyVroomImport(vroomPreview.result);
    setVroomPreview(null);
  };

  // Chiffres du récapitulatif, calculés sur la version affichée : libellés des
  // champs tels que le formulaire les montre, et champs obligatoires qui
  // resteront vides une fois les valeurs par défaut et l'import appliqués.
  const vroomPreviewSummary = useMemo(() => {
    if (!vroomPreview) return null;
    const { result: importResult } = vroomPreview;
    const fieldsBySlug = new Map(formFields.map((field) => [field.slug, field]));
    const importedSlugs = Object.keys(importResult.inputs).filter(
      (slug) => fieldsBySlug.get(slug)?.field_type !== "checkbox"
    );
    return {
      nbValues: importedSlugs.length,
      fieldLabels: Array.from(new Set(importedSlugs.map((slug) => fieldsBySlug.get(slug)?.label ?? slug))),
      nbMissingRequired: findMissingRequiredFieldsForInputs({ ...formDefaultValues, ...importResult.inputs }).length,
      nbPoints: importResult.circuits.reduce((total, circuit) => total + circuit.nb_points, 0),
      totalKm: importResult.circuits.reduce((total, circuit) => total + circuit.distance_km, 0),
      models: Array.from(new Set(importResult.circuits.map((circuit) => circuit.modele).filter(Boolean))),
    };
  }, [vroomPreview, formFields, formDefaultValues, findMissingRequiredFieldsForInputs]);

  const formatDuration = (minutes: number) => {
    const total = Math.round(minutes);
    return `${Math.floor(total / 60)} h ${String(total % 60).padStart(2, "0")}`;
  };

  // Load simulation inputs from sessionStorage or API when coming from simulation history
  useEffect(() => {
    if (!loadSimulationId || loadingFields) return;

    const shouldLoadValue = (value: unknown): boolean => {
      if (value === null || value === undefined) return false;
      if (value === "") return false;
      if (value === false) return false;
      if (value === 0) return false;
      if (Array.isArray(value) && (value.length === 0 || value.every(v => v === "" || v === null))) return false;
      return true;
    };

    const restoreInputs = (inputs: Record<string, unknown>) => {
      const timeoutId = setTimeout(() => {
        isRestoringRef.current = true;
        Object.entries(inputs).forEach(([key, value]) => {
          if (shouldLoadValue(value)) {
            setValue(key, value);
          }
        });
        setTimeout(() => { isRestoringRef.current = false; }, 0);
        setIsDraftLoaded(true);

        setTimeout(() => {
          evaluateAllFieldWarnings(inputs);
          evaluateAllCrossFieldWarnings(inputs);
          setCalculatedInputs({ ...inputs });

          // C9 — On the reopen of a historical cotation we no longer auto-fire
          // `executeMutation`. Instead we run the same validation passes that
          // gate a manual « Calculer » click and surface every missing /
          // invalid field inline (red ring on the owning group + red message
          // under each input). The user reviews & completes the form, then
          // clicks « Calculer » themselves. No toast, no backend call.
          const currentValues = getValues();
          const groupValidation = validateGroupsBeforeSubmit(currentValues);
          const missingFieldRequired = findMissingRequiredFieldsForInputs(currentValues);

          const mergedFieldErrors: Record<string, string> = { ...groupValidation.errorFieldSlugs };
          const mergedGroupErrors: Record<string, string> = { ...groupValidation.errorGroupSlugs };

          for (const f of missingFieldRequired) {
            if (!mergedFieldErrors[f.slug]) {
              mergedFieldErrors[f.slug] = "Ce champ est requis";
            }
            const owning = findOwningGroup(formGroups, f.slug);
            if (owning && !mergedGroupErrors[owning.slug]) {
              mergedGroupErrors[owning.slug] = "__RING_ONLY__";
            }
          }

          setFieldValidationErrors(mergedFieldErrors);
          setGroupValidationErrors(mergedGroupErrors);
        }, 200);
      }, 100);
      return timeoutId;
    };

    const storageKey = `simulation_inputs_${loadSimulationId}`;
    const storedInputs = sessionStorage.getItem(storageKey);

    if (storedInputs) {
      let inputs: Record<string, unknown> | null = null;
      try {
        inputs = JSON.parse(storedInputs);
      } catch (error) {
        console.error("Failed to parse simulation inputs:", error);
        sessionStorage.removeItem(storageKey);
        return;
      }

      if (!inputs || typeof inputs !== "object") {
        sessionStorage.removeItem(storageKey);
        return;
      }

      sessionStorage.removeItem(storageKey);
      const timeoutId = restoreInputs(inputs);
      return () => clearTimeout(timeoutId);
    }

    // Fallback: fetch from API when sessionStorage is empty (e.g. direct URL navigation)
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    getSimulation(Number(loadSimulationId))
      .then((simulation) => {
        if (cancelled) return;
        if (simulation.inputs && typeof simulation.inputs === "object") {
          if (simulation.notes) {
            setNotes(simulation.notes);
          }
          timeoutId = restoreInputs(simulation.inputs);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Failed to load simulation from API:", error);
          toast.error("Impossible de charger la simulation");
        }
      });

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [loadSimulationId, loadTimestamp, loadingFields, setValue, getValues, validateGroupsBeforeSubmit, findMissingRequiredFieldsForInputs, findOwningGroup, formGroups, setFieldValidationErrors, setGroupValidationErrors]);

  // Load simulation event inputs from sessionStorage or API when reopening a
  // simulation_event row from "Mes Simulations" history.
  useEffect(() => {
    if (!loadEventId || loadingFields) return;

    const shouldLoadValue = (value: unknown): boolean => {
      if (value === null || value === undefined) return false;
      if (value === "") return false;
      if (value === false) return false;
      if (value === 0) return false;
      if (Array.isArray(value) && (value.length === 0 || value.every(v => v === "" || v === null))) return false;
      return true;
    };

    const restoreInputs = (inputs: Record<string, unknown>) => {
      const timeoutId = setTimeout(() => {
        isRestoringRef.current = true;
        Object.entries(inputs).forEach(([key, value]) => {
          if (shouldLoadValue(value)) {
            setValue(key, value);
          }
        });
        setTimeout(() => { isRestoringRef.current = false; }, 0);
        setIsDraftLoaded(true);

        setTimeout(() => {
          evaluateAllFieldWarnings(inputs);
          evaluateAllCrossFieldWarnings(inputs);
          setCalculatedInputs({ ...inputs });

          // Same C9 inline-marking pass as for cotations: a historical event
          // may predate newly added required fields or group rules. We mark
          // them inline (red ring + red message) and let the user click
          // « Calculer » themselves. No toast, no backend call.
          const currentValues = getValues();
          const groupValidation = validateGroupsBeforeSubmit(currentValues);
          const missingFieldRequired = findMissingRequiredFieldsForInputs(currentValues);

          const mergedFieldErrors: Record<string, string> = { ...groupValidation.errorFieldSlugs };
          const mergedGroupErrors: Record<string, string> = { ...groupValidation.errorGroupSlugs };

          for (const f of missingFieldRequired) {
            if (!mergedFieldErrors[f.slug]) {
              mergedFieldErrors[f.slug] = "Ce champ est requis";
            }
            const owning = findOwningGroup(formGroups, f.slug);
            if (owning && !mergedGroupErrors[owning.slug]) {
              mergedGroupErrors[owning.slug] = "__RING_ONLY__";
            }
          }

          setFieldValidationErrors(mergedFieldErrors);
          setGroupValidationErrors(mergedGroupErrors);
        }, 200);
      }, 100);
      return timeoutId;
    };

    const storageKey = `simulation_event_inputs_${loadEventId}`;
    const storedInputs = sessionStorage.getItem(storageKey);

    if (storedInputs) {
      let inputs: Record<string, unknown> | null = null;
      try {
        inputs = JSON.parse(storedInputs);
      } catch (error) {
        console.error("Failed to parse simulation event inputs:", error);
        sessionStorage.removeItem(storageKey);
        return;
      }

      if (!inputs || typeof inputs !== "object") {
        sessionStorage.removeItem(storageKey);
        return;
      }

      sessionStorage.removeItem(storageKey);
      const timeoutId = restoreInputs(inputs);
      return () => clearTimeout(timeoutId);
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    getSimulationEvent(Number(loadEventId))
      .then((event) => {
        if (cancelled) return;
        if (event.inputs && typeof event.inputs === "object") {
          timeoutId = restoreInputs(event.inputs as Record<string, unknown>);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Failed to load simulation event from API:", error);
          toast.error("Impossible de charger la simulation");
        }
      });

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [loadEventId, loadTimestamp, loadingFields, setValue, getValues, validateGroupsBeforeSubmit, findMissingRequiredFieldsForInputs, findOwningGroup, formGroups, setFieldValidationErrors, setGroupValidationErrors]);

  // Saisie sérialisée : c'est elle, et non `watchedValues`, qui déclenche la
  // sauvegarde du brouillon. `watch()` rend un nouvel objet à chaque rendu et
  // l'enregistrement d'un brouillon provoque lui-même un rendu : en dépendant de
  // l'objet, l'effet se relançait sans fin — un PUT par seconde, page ouverte.
  const draftValues = JSON.stringify({ inputs: watchedValues, notes });

  // Auto-save draft effect - saves form inputs to API (only in manual mode with auto_save_draft enabled)
  useEffect(() => {
    // Only save in manual mode with auto_save_draft enabled
    if (autoCalculate || !autoSaveDraft || !selectedVersionId || loadingFields || !isDraftLoaded) return;

    // Skip if no values have been entered yet
    const hasAnyValue = Object.values(watchedValues).some(
      (v) => v !== undefined && v !== null && v !== ""
    );
    if (!hasAnyValue) return;

    // Clear any existing timer
    if (draftSaveTimerRef.current) {
      clearTimeout(draftSaveTimerRef.current);
    }

    // Set debounced save (1 second delay)
    draftSaveTimerRef.current = setTimeout(async () => {
      try {
        const draft = await saveDraft(selectedVersionId, {
          inputs: watchedValues,
          notes: notes || undefined,
          // Include calculation results if available
          outputs: result?.success ? result.outputs : undefined,
          execution_time_ms: result?.success ? result.execution_time_ms : undefined,
        });
        setDraftSavedAt(draft.updated_at);
      } catch (error) {
        console.error("Failed to save draft:", error);
      }
    }, 1000);

    return () => {
      if (draftSaveTimerRef.current) {
        clearTimeout(draftSaveTimerRef.current);
      }
    };
  }, [draftValues, result, autoCalculate, autoSaveDraft, selectedVersionId, loadingFields, isDraftLoaded]);

  // Helper function to clear draft from API
  const clearDraft = useCallback(async () => {
    if (!selectedVersionId) return;
    try {
      await deleteDraft(selectedVersionId);
      setDraftSavedAt(null);
    } catch (error) {
      // Ignore 404 errors (draft already deleted or doesn't exist)
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status === 404) {
          setDraftSavedAt(null);
          return;
        }
      }
      console.error("Failed to clear draft:", error);
    }
  }, [selectedVersionId]);

  // Track if there are unsaved changes (for visual indicator)
  const [lastSavedValues, setLastSavedValues] = useState<string | null>(null);
  const hasUnsavedChanges = useMemo(() => {
    if (!autoSaveDraft || autoCalculate) return false;
    return lastSavedValues !== null && draftValues !== lastSavedValues;
  }, [draftValues, lastSavedValues, autoSaveDraft, autoCalculate]);

  // Update lastSavedValues when draft is saved
  useEffect(() => {
    if (draftSavedAt) {
      setLastSavedValues(draftValues);
    }
  }, [draftSavedAt]);

  // Manual save draft function
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const handleSaveDraftManually = useCallback(async () => {
    if (!selectedVersionId || isSavingDraft) return;
    
    setIsSavingDraft(true);
    try {
      const draft = await saveDraft(selectedVersionId, {
        inputs: watchedValues,
        notes: notes || undefined,
        // Include calculation results if available
        outputs: result?.success ? result.outputs : undefined,
        execution_time_ms: result?.success ? result.execution_time_ms : undefined,
      });
      setDraftSavedAt(draft.updated_at);
      toast.success("Brouillon sauvegardé");
    } catch (error) {
      console.error("Failed to save draft:", error);
      toast.error("Erreur lors de la sauvegarde du brouillon");
    } finally {
      setIsSavingDraft(false);
    }
  }, [selectedVersionId, watchedValues, notes, result, isSavingDraft]);

  // Helper to get form groups grid class based on appearance options
  const getFormGroupsGridClass = () => {
    switch (formGroupsPerRow) {
      case 2: return "grid grid-cols-1 lg:grid-cols-2 gap-4";
      case 3: return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4";
      default: return "space-y-4";
    }
  };

  // Helper to get individual group wrapper class (for full_width support)
  const getGroupWrapperClass = (group: FormFieldGroupWithFields) => {
    const isFullWidth = group.layout_options?.full_width === true;
    if (formGroupsPerRow === 1) return ""; // No special class needed for single column
    if (isFullWidth) {
      // Force full width by spanning all columns
      switch (formGroupsPerRow) {
        case 2: return "lg:col-span-2";
        case 3: return "md:col-span-2 lg:col-span-3";
        default: return "";
      }
    }
    return "";
  };

  // Helper to get output groups grid class based on appearance options
  const getOutputGroupsGridClass = () => {
    switch (outputGroupsPerRow) {
      case 2: return "grid grid-cols-1 lg:grid-cols-2 gap-4";
      case 3: return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4";
      default: return "space-y-6";
    }
  };

  // Helper to get main container grid class
  const getMainGridClass = () => {
    if (resultsPosition === "bottom") {
      return "flex flex-col gap-6";
    }
    // Right position
    switch (resultsWidth) {
      case "third": return "grid grid-cols-1 lg:grid-cols-12 gap-6";
      case "full": return "flex flex-col gap-6";
      default: return "grid grid-cols-1 lg:grid-cols-2 gap-6";
    }
  };

  // Helper to get form section class
  const getFormSectionClass = () => {
    if (resultsPosition === "bottom" || resultsWidth === "full") return "";
    if (resultsWidth === "third") return "lg:col-span-8";
    return "";
  };

  // Helper to get results section class
  const getResultsSectionClass = () => {
    if (resultsPosition === "bottom" || resultsWidth === "full") return "";
    if (resultsWidth === "third") return "lg:col-span-4";
    return "";
  };

  // Group output fields by their groups (with layout options for styling)
  const groupedOutputs = useMemo(() => {
    if (!outputFieldGroups) return [] as { name: string; fields: OutputField[]; backgroundColor?: string }[];
    const result: { name: string; fields: OutputField[]; backgroundColor?: string }[] = [];
    
    const processGroup = (group: OutputFieldGroupWithFields, parentName?: string) => {
      const hideParentName = group.layout_options?.hide_parent_name === true;
      const groupName = (parentName && !hideParentName) ? `${parentName} > ${group.name}` : group.name;
      // Include all fields - filtering is done at render time to support "show all"
      if (group.fields.length > 0) {
        result.push({
          name: groupName,
          fields: group.fields,
          backgroundColor: group.layout_options?.background_color,
        });
      }
      group.children.forEach(child => processGroup(child, groupName));
    };
    
    outputFieldGroups.forEach(g => processGroup(g));
    return result;
  }, [outputFieldGroups]);

  // Check if a field should be visible based on its visibility conditions
  const isFieldVisible = useCallback((field: FormField): boolean => {
    if (field.visible === false) {
      return false;
    }
    if (!field.visibility_conditions || field.visibility_conditions.conditions.length === 0) {
      return true;
    }

    const { logic, conditions } = field.visibility_conditions;
    
    if (logic === "AND") {
      return conditions.every((condition) => evaluateCondition(condition, watchedValues));
    } else {
      return conditions.some((condition) => evaluateCondition(condition, watchedValues));
    }
  }, [watchedValues, evaluateCondition]);

  // Check if a group should be visible based on its visibility conditions
  const isGroupVisible = useCallback((group: FormFieldGroupWithFields): boolean => {
    if (!group.visibility_conditions || group.visibility_conditions.conditions.length === 0) {
      return true;
    }

    const { logic, conditions } = group.visibility_conditions;
    
    if (logic === "AND") {
      return conditions.every((condition) => evaluateCondition(condition, watchedValues));
    } else {
      return conditions.some((condition) => evaluateCondition(condition, watchedValues));
    }
  }, [watchedValues, evaluateCondition]);

  // Calculate navigation status for each visible group (only form-context groups)
  const groupNavigation = useMemo(() => {
    if (!formGroups.length) return [];

    // Helper to check if a group is visible
    const checkGroupVisible = (group: FormFieldGroupWithFields): boolean => {
      if (!group.visibility_conditions || group.visibility_conditions.conditions.length === 0) {
        return true;
      }
      const { logic, conditions } = group.visibility_conditions;
      const evalCond = (cond: FieldCondition): boolean => {
        const fieldValue = watchedValues[cond.field_slug];
        const fieldMap = displayToValueMap[cond.field_slug];
        const resolvedTarget = (typeof cond.value === "string" && fieldMap?.[cond.value]) || cond.value;
        switch (cond.operator) {
          case "equals": return String(fieldValue) === String(resolvedTarget);
          case "not_equals": return String(fieldValue) !== String(resolvedTarget);
          case "is_empty": return fieldValue === undefined || fieldValue === null || fieldValue === "";
          case "is_not_empty": return fieldValue !== undefined && fieldValue !== null && fieldValue !== "";
          default: return true;
        }
      };
      return logic === "AND" ? conditions.every(evalCond) : conditions.some(evalCond);
    };

    // Helper to check if a field is visible
    const checkFieldVisible = (field: FormField): boolean => {
      if (field.visible === false) return false;
      if (!field.visibility_conditions || field.visibility_conditions.conditions.length === 0) return true;
      const { logic, conditions } = field.visibility_conditions;
      const evalCond = (cond: FieldCondition): boolean => {
        const fieldValue = watchedValues[cond.field_slug];
        const fieldMap = displayToValueMap[cond.field_slug];
        const resolvedTarget = (typeof cond.value === "string" && fieldMap?.[cond.value]) || cond.value;
        switch (cond.operator) {
          case "equals": return String(fieldValue) === String(resolvedTarget);
          case "not_equals": return String(fieldValue) !== String(resolvedTarget);
          case "is_empty": return fieldValue === undefined || fieldValue === null || fieldValue === "";
          case "is_not_empty": return fieldValue !== undefined && fieldValue !== null && fieldValue !== "";
          default: return true;
        }
      };
      return logic === "AND" ? conditions.every(evalCond) : conditions.some(evalCond);
    };

    // Get status for a group
    const getGroupStatus = (group: FormFieldGroupWithFields): 'complete' | 'error' | 'incomplete' => {
      // Check for group validation errors
      if (groupValidationErrors[group.slug]) {
        return 'error';
      }

      // Get visible required fields in this group
      const visibleRequiredFields = group.fields.filter(f => f.required && checkFieldVisible(f));
      
      // Check for field errors
      const hasErrors = visibleRequiredFields.some(f => errors[f.slug]);
      if (hasErrors && isSubmitted) {
        return 'error';
      }

      // Check if all required fields are filled
      const allFilled = visibleRequiredFields.every(f => isValueFilled(watchedValues[f.slug]));
      if (allFilled && visibleRequiredFields.length > 0) {
        return 'complete';
      }

      return 'incomplete';
    };

    return formGroups
      .filter(checkGroupVisible)
      .map(group => ({
        slug: group.slug,
        name: stripBBCode(group.name),
        status: getGroupStatus(group),
      }));
  }, [formGroups, watchedValues, errors, isSubmitted, groupValidationErrors, displayToValueMap]);

  // Scroll to a group when clicking on navigation
  const handleNavClick = useCallback((groupSlug: string) => {
    const element = document.getElementById(`group-${groupSlug}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Evaluate cross-field warning rules when a trigger field loses focus
  const evaluateCrossFieldWarnings = useCallback((triggerFieldSlug: string) => {
    if (!crossFieldWarningRules || crossFieldWarningRules.length === 0) return;
    
    const newWarnings: Record<string, string | null> = {};
    const newErrors: Record<string, string | null> = {};
    const newGroupErrors: Record<string, string> = {};
    const touchedGroupSlugs = new Set<string>();
    const parser = new Parser();
    // `getValues()` et non `watchedValues` : les handlers de champ appellent cette
    // fonction juste après `onChange`, avant que le re-render n'ait rafraîchi
    // `watchedValues`. Un snapshot périmé évaluait la règle sur l'ancienne valeur
    // et affichait le message à contretemps (une fois la case cochée).
    const context = buildEvalContext(getValues());
    
    for (const rule of crossFieldWarningRules) {
      if (rule.enabled === false) continue;
      if (!rule.trigger_fields?.includes(triggerFieldSlug)) continue;
      
      for (const gs of rule.target_groups ?? []) {
        touchedGroupSlugs.add(gs);
      }
      
      const isError = rule.severity === "error";
      const targetMap = isError ? newErrors : newWarnings;
      
      try {
        const expr = parser.parse(rule.condition);
        const result = expr.evaluate(context);
        
        for (const targetField of rule.target_fields ?? []) {
          if (result) {
            targetMap[targetField] = rule.message;
          } else if (!(targetField in targetMap)) {
            targetMap[targetField] = null;
          }
        }
        
        if (result) {
          for (const gs of rule.target_groups ?? []) {
            newGroupErrors[gs] = rule.message;
          }
        }
      } catch (error) {
        console.error(`[CrossFieldWarnings] Error evaluating rule: ${rule.condition}`, error);
        for (const targetField of rule.target_fields ?? []) {
          if (!(targetField in targetMap)) {
            targetMap[targetField] = null;
          }
        }
      }
    }
    
    setCrossFieldWarnings(prev => ({ ...prev, ...newWarnings }));
    setCrossFieldErrors(prev => ({ ...prev, ...newErrors }));
    if (touchedGroupSlugs.size > 0) {
      setGroupValidationErrors(prev => {
        const updated = { ...prev };
        for (const gs of touchedGroupSlugs) delete updated[gs];
        return { ...updated, ...newGroupErrors };
      });
    }
  }, [crossFieldWarningRules, getValues, buildEvalContext]);

  // Consignes visibles d'emblée (opt-in par simulateur) : sans cette passe, une
  // règle inline n'apparaît qu'au premier changement d'un `trigger_fields`, donc
  // jamais tant que l'utilisateur n'a rien touché.
  const evaluateOnLoad = simulatorSettings.evaluate_warnings_on_load ?? false;
  const warningsEvaluatedOnLoadRef = useRef(false);
  useEffect(() => {
    if (!evaluateOnLoad || warningsEvaluatedOnLoadRef.current) return;
    if (crossFieldWarningRules.length === 0) return;
    warningsEvaluatedOnLoadRef.current = true;
    evaluateAllCrossFieldWarnings(getValues());
  }, [evaluateOnLoad, crossFieldWarningRules, evaluateAllCrossFieldWarnings, getValues]);

  // Check if an output field should be visible based on its visibility conditions
  // Uses calculatedInputs (values at time of calculation) instead of current form values
  // This prevents outputs from disappearing when the user changes inputs after a calculation
  const isOutputFieldVisible = useCallback((field: OutputField): boolean => {
    // If no calculation has been done yet, use current form values as fallback.
    // Computed outputs are merged in so visibility conditions can reference an
    // output slug (e.g. hide "Forfait mensuel" when cout_total_contrat < 10000).
    // Inputs take precedence to preserve existing input-based conditions.
    const valuesToCheck = { ...(result?.outputs ?? {}), ...(calculatedInputs ?? watchedValues) };
    
    // Check visibility mode
    switch (field.visibility_mode) {
      case "never":
        return false;
      case "always":
        return true;
      case "conditional":
        // Evaluate visibility conditions using the inputs from the last calculation
        if (!field.visibility_conditions || field.visibility_conditions.conditions.length === 0) {
          return true; // No conditions = show
        }
        const { logic, conditions } = field.visibility_conditions;
        if (logic === "AND") {
          return conditions.every((condition) => evaluateCondition(condition, valuesToCheck));
        } else {
          return conditions.some((condition) => evaluateCondition(condition, valuesToCheck));
        }
      default:
        return true; // Default to visible
    }
  }, [calculatedInputs, watchedValues, evaluateCondition, result]);

  // Get ALL matching conditional constraints for a field based on source field value
  // Returns an array to support multiple rules with the same when_value (e.g., multiple reference tables)
  const getAppliedConstraints = useCallback((field: FormField): ConditionalRule[] => {
    if (!field.conditional_constraints) return [];
    
    const { source_field_slug, rules } = field.conditional_constraints;
    const sourceValue = watchedValues[source_field_slug];
    
    // Handle undefined/null but NOT empty string or false (which are valid values)
    if (sourceValue === undefined || sourceValue === null) {
      return [];
    }
    
    // Find ALL rules that match the source field value
    // Handle boolean values (from checkbox fields) and other types
    const matchingRules = rules.filter((rule) => {
      // For boolean comparisons
      if (typeof rule.when_value === "boolean" || typeof sourceValue === "boolean") {
        return rule.when_value === sourceValue;
      }
      // For string/number comparisons
      return String(rule.when_value) === String(sourceValue);
    });
    return matchingRules;
  }, [watchedValues]);

  const formatOutput = (field: OutputField, value: unknown) => {
    if (value === null || value === undefined) return "-";
    
    let formatted: string;
    switch (field.output_type) {
      case "currency":
        formatted = formatCurrency(Number(value));
        break;
      case "percentage":
        formatted = `${formatNumber(Number(value) * 100, field.decimal_places ?? 1)}%`;
        break;
      case "number":
        formatted = formatNumber(Number(value), field.decimal_places ?? 2);
        break;
      case "boolean":
        formatted = value ? "Oui" : "Non";
        break;
      case "text":
        formatted = String(value);
        break;
      case "date":
        formatted = String(value);
        break;
      case "json":
        formatted = typeof value === "object" ? JSON.stringify(value, null, 2) : String(value);
        break;
      default:
        formatted = String(value);
    }
    
    // Apply prefix and suffix if configured
    const prefix = field.prefix || "";
    const suffix = field.suffix || "";
    return `${prefix}${formatted}${suffix}`;
  };

  // Helper function to check if a value is considered "zero" for filtering
  const isZeroValue = (value: unknown): boolean => {
    if (value === null || value === undefined) return true;
    if (typeof value === "number") return value === 0;
    if (typeof value === "string") return value === "0" || value === "0.0" || value === "0,0" || value === "";
    return false;
  };

  // Get options for a select/multiselect field (from enum_values or reference table)
  // Also applies conditional constraints if defined (including dynamic reference tables)
  // Supports multiple rules with the same when_value - options from all matching tables are concatenated
  // allowed_values in constraints are ADDED to options (not used as filter)
  // Returns {label, value} pairs: label is for display (reference_column), value is for storage (reference_value_column)
  const getSelectOptions = useCallback((field: FormField): { label: string; value: string }[] => {
    let options: { label: string; value: string }[] = [];
    
    // Get ALL applied conditional constraints (can be multiple rules with same when_value)
    const appliedConstraints = getAppliedConstraints(field);
    
    // Check if any conditional constraints define reference tables
    const constraintsWithRefTables = appliedConstraints.filter(
      (c) => c.reference_table_id && c.reference_column
    );
    
    if (constraintsWithRefTables.length > 0 && referenceTables) {
      const allOptions: { label: string; value: string }[] = [];
      for (const constraint of constraintsWithRefTables) {
        const table = findReferenceTable(referenceTables, constraint.reference_table_id);
        if (table?.data) {
          const displayColumn = constraint.reference_column!;
          const valueColumn = constraint.reference_value_column || displayColumn;
          const tableOptions = table.data
            .filter((row) => {
              const val = row[displayColumn];
              return val !== null && val !== undefined && val !== "";
            })
            .map((row) => ({
              label: String(row[displayColumn]),
              value: String(row[valueColumn] ?? row[displayColumn]),
            }));
          allOptions.push(...tableOptions);
        }
      }
      options = allOptions;
    }
    // If field has enum_values, use them directly
    else if (field.enum_values && field.enum_values.length > 0) {
      options = field.enum_values.filter((v) => v !== "").map((v) => ({ label: v, value: v }));
    }
    // If field references a table, get options from reference table data
    else if (field.reference_table_id && field.reference_column && referenceTables) {
      const table = findReferenceTable(referenceTables, field.reference_table_id);
      if (table?.data) {
        let filteredData = table.data;
        if (field.reference_filter?.column && field.reference_filter.values.length > 0) {
          filteredData = table.data.filter((row) => {
            const cellValue = String(row[field.reference_filter!.column] ?? "");
            return field.reference_filter!.values.includes(cellValue);
          });
        }

        const displayColumn = field.reference_column!;
        const valueColumn = field.reference_value_column || displayColumn;
        const seen = new Set<string>();
        options = filteredData
          .filter((row) => {
            const val = row[displayColumn];
            return val !== null && val !== undefined && val !== "";
          })
          .map((row) => ({
            label: String(row[displayColumn]),
            value: String(row[valueColumn] ?? row[displayColumn]),
          }))
          .filter((opt) => {
            if (seen.has(opt.value) || opt.value === "") return false;
            seen.add(opt.value);
            return true;
          });
      }
    }

    // Add allowed_values from conditional constraints to the options
    // This allows adding extra options (like "Autre véhicule") on top of reference table values
    for (const constraint of appliedConstraints) {
      if (constraint.allowed_values && constraint.allowed_values.length > 0) {
        options.push(...constraint.allowed_values.map((v) => ({ label: v, value: v })));
      }
    }

    // Return unique options by value (excluding empty values)
    const seen = new Set<string>();
    return options.filter((opt) => {
      if (seen.has(opt.value) || opt.value === "") return false;
      seen.add(opt.value);
      return true;
    });
  }, [referenceTables, getAppliedConstraints]);

  const renderField = (field: FormField) => {
    const commonProps = {
      id: field.slug,
      placeholder: field.placeholder || undefined,
    };

    // Get applied conditional constraints for this field (take first for min/max constraints)
    const appliedConstraints = getAppliedConstraints(field);
    const firstConstraint = appliedConstraints[0] || null;

    switch (field.field_type) {
      case "number":
        // Use conditional constraints min/max if available, otherwise use field defaults
        const effectiveMin = firstConstraint?.min_value ?? field.min_value ?? undefined;
        const effectiveMax = firstConstraint?.max_value ?? field.max_value ?? undefined;
        const integerOnly = field.integer_only === true;
        
        // Generate placeholder with constraints info
        const constraintPlaceholder = firstConstraint && (effectiveMin !== undefined || effectiveMax !== undefined)
          ? (effectiveMin !== undefined && effectiveMax !== undefined
              ? `Valeur entre ${effectiveMin} et ${effectiveMax}`
              : effectiveMin !== undefined
              ? `Valeur minimum: ${effectiveMin}`
              : `Valeur maximum: ${effectiveMax}`)
          : undefined;
        const numberPlaceholder = field.placeholder || constraintPlaceholder || undefined;
        
        return (
          <Controller
            name={field.slug}
            control={control}
            defaultValue={field.default_value ?? ""}
            rules={{ 
              required: field.required,
              validate: (value) => {
                if (value === "" || value === undefined || value === null) return true;
                const numValue = parseFloat(String(value));
                if (isNaN(numValue)) return true;
                if (integerOnly && !Number.isInteger(numValue)) {
                  return "La valeur doit être un nombre entier";
                }
                if (effectiveMin !== undefined && numValue < effectiveMin) {
                  return `La valeur doit être >= ${effectiveMin}`;
                }
                if (effectiveMax !== undefined && numValue > effectiveMax) {
                  return `La valeur doit être <= ${effectiveMax}`;
                }
                return true;
              }
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                {...commonProps}
                type="number"
                step={integerOnly ? "1" : "any"}
                placeholder={numberPlaceholder}
                value={value as string | number | undefined}
                onKeyDown={(e) => {
                  // Block decimal separators when the field is integer-only.
                  // "e" / "E" are also blocked to avoid scientific notation entering decimals.
                  if (integerOnly && (e.key === "." || e.key === "," || e.key === "e" || e.key === "E")) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") { onChange(""); return; }
                  if (effectiveMin !== undefined && effectiveMin >= 0 && raw.includes("-")) return;
                  if (integerOnly && /[.,]/.test(raw)) return;
                  const num = integerOnly ? parseInt(raw, 10) : parseFloat(raw);
                  if (isNaN(num)) return;
                  onChange(num);
                }}
                onBlur={() => {
                  if (value !== "" && value !== undefined && value !== null) {
                    let num = typeof value === "number" ? value : parseFloat(String(value));
                    if (!isNaN(num)) {
                      if (integerOnly && !Number.isInteger(num)) {
                        num = Math.trunc(num);
                        onChange(num);
                      }
                      if (effectiveMin !== undefined && num < effectiveMin) onChange(effectiveMin);
                      else if (effectiveMax !== undefined && num > effectiveMax) onChange(effectiveMax);
                    }
                  }
                  const warning = checkRecommendedValue(field, value);
                  setFieldWarnings(prev => ({ ...prev, [field.slug]: warning }));
                  evaluateCrossFieldWarnings(field.slug);
                }}
                onPaste={(e) => {
                  // Strip decimal separators on paste when integer-only is enabled
                  if (!integerOnly) return;
                  const text = e.clipboardData.getData("text");
                  if (/[.,]/.test(text)) {
                    e.preventDefault();
                    const cleaned = text.replace(/[.,].*$/, "");
                    if (cleaned !== "") {
                      const num = parseInt(cleaned, 10);
                      if (!isNaN(num)) onChange(num);
                    }
                  }
                }}
                min={effectiveMin}
                max={effectiveMax}
                className={`${error ? "border-destructive" : ""} ${!showSpinbox ? "no-spinbox" : ""}`}
              />
            )}
          />
        );

      case "textarea":
        return (
          <Controller
            name={field.slug}
            control={control}
            defaultValue={field.default_value ?? ""}
            rules={{ required: field.required }}
            render={({ field: { onChange, value } }) => (
              <Textarea
                {...commonProps}
                value={value as string | undefined}
                onChange={onChange}
                onBlur={() => evaluateCrossFieldWarnings(field.slug)}
                rows={3}
              />
            )}
          />
        );

      case "select":
        const selectOptions = getSelectOptions(field);
        return (
          <Controller
            name={field.slug}
            control={control}
            defaultValue={field.default_value ?? ""}
            rules={{ required: field.required }}
            render={({ field: { onChange, value } }) => {
              // Ensure the value is valid - if not in options, reset to empty or default
              // This prevents Radix Select crash when options change dynamically
              const stringValue = value as string;
              const isValueValid = selectOptions.some((o) => o.value === stringValue);
              const isValueEmpty = stringValue === "" || value === undefined || value === null;
              
              // Check if field has a valid default value in current options
              const hasValidDefault = field.default_value && 
                field.default_value !== "" && 
                selectOptions.some((o) => o.value === String(field.default_value));
              
              // Determine the valid value to display
              let validValue = "";
              if (isValueValid) {
                validValue = stringValue;
              } else if (isValueEmpty && hasValidDefault) {
                // Value is empty but we have a valid default - restore it
                validValue = String(field.default_value);
                setTimeout(() => onChange(validValue), 0);
              }
              
              // If current value is not valid, not empty, and not the default being restored, reset it
              // Skip during restoration to preserve saved values (options may not be computed yet)
              if (!isValueValid && !isValueEmpty && !isRestoringRef.current) {
                setTimeout(() => onChange(""), 0);
              }
              
              // Check if there's a selected value to show clear button
              const hasValue = validValue !== "" && validValue !== undefined && validValue !== null;
              
              return (
                <div id={field.slug} tabIndex={-1}>
                  <Select 
                    value={validValue} 
                    onValueChange={(newValue) => {
                      onChange(newValue);
                      // Evaluate cross-field warnings after value change for select
                      setTimeout(() => evaluateCrossFieldWarnings(field.slug), 0);
                    }}
                  >
                    <ClearableSelectTrigger
                      showClear={hasValue && !field.required}
                      onClear={() => {
                        onChange("");
                        setTimeout(() => evaluateCrossFieldWarnings(field.slug), 0);
                      }}
                    >
                      <SelectValue placeholder="Sélectionner..." />
                    </ClearableSelectTrigger>
                    <SelectContent>
                      {selectOptions.length === 0 ? (
                        <SelectItem value="__empty__" disabled>
                          Aucune option disponible
                        </SelectItem>
                      ) : (
                        selectOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              );
            }}
          />
        );

      case "checkbox": {
        const disableTooltip = disabledCheckboxMap[field.slug];
        const isDisabledByRule = Boolean(disableTooltip);
        const checkboxEl = (
          <Controller
            name={field.slug}
            control={control}
            defaultValue={field.default_value ?? false}
            render={({ field: { onChange, value } }) => (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={field.slug}
                  checked={value as boolean | undefined}
                  disabled={isDisabledByRule && !value}
                  onCheckedChange={(checked) => {
                    if (isDisabledByRule && !value) return;
                    onChange(checked);
                    setTimeout(() => evaluateCrossFieldWarnings(field.slug), 0);
                  }}
                />
                <Label
                  htmlFor={field.slug}
                  className={`font-normal ${getLabelClassName(field.label_style)} ${isDisabledByRule && !value ? "text-muted-foreground cursor-not-allowed" : ""}`}
                  style={getLabelInlineStyle(field.label_style)}
                >
                  {parseBBCode(field.label)}
                </Label>
                {field.tooltip && (
                  <InfoTooltip content={field.tooltip} side="top">
                    <button
                      type="button"
                      className="text-muted-foreground cursor-help hover:text-foreground transition-colors flex-shrink-0"
                    >
                      ⓘ
                    </button>
                  </InfoTooltip>
                )}
              </div>
            )}
          />
        );
        if (isDisabledByRule) {
          return (
            <InfoTooltip content={disableTooltip} side="top">
              <div className="inline-block">{checkboxEl}</div>
            </InfoTooltip>
          );
        }
        return checkboxEl;
      }

      case "radio":
        const radioOptions = getSelectOptions(field);
        const radioParentGroup = groupsById.get(field.group_id);
        const radioLayout = radioParentGroup?.layout_options?.radio_layout;
        const radioGroupClassName =
          radioLayout === "grid-3"
            ? "grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2"
            : radioLayout === "grid-2"
            ? "grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2"
            : radioLayout === "inline"
            ? "flex flex-wrap gap-x-6 gap-y-2"
            : "flex flex-col space-y-2";
        return (
          <Controller
            name={field.slug}
            control={control}
            defaultValue={field.default_value ?? ""}
            rules={{ required: field.required }}
            render={({ field: { onChange, value } }) => {
              // Ensure the value is valid - if not in options, reset to empty or default
              const stringValue = value as string;
              const isValueValid = radioOptions.some((o) => o.value === stringValue);
              const isValueEmpty = stringValue === "" || value === undefined || value === null;
              
              // Check if field has a valid default value in current options
              const hasValidDefault = field.default_value && 
                field.default_value !== "" && 
                radioOptions.some((o) => o.value === String(field.default_value));
              
              // Determine the valid value to display
              let validValue = "";
              if (isValueValid) {
                validValue = stringValue;
              } else if (isValueEmpty && hasValidDefault) {
                // Value is empty but we have a valid default - restore it
                validValue = String(field.default_value);
                setTimeout(() => onChange(validValue), 0);
              }
              
              // If current value is not valid, not empty, reset it
              if (!isValueValid && !isValueEmpty) {
                setTimeout(() => onChange(""), 0);
              }
              
              return (
                <RadioGroup
                  id={field.slug}
                  value={validValue}
                  onValueChange={(newValue) => {
                    onChange(newValue);
                    setTimeout(() => evaluateCrossFieldWarnings(field.slug), 0);
                  }}
                  className={radioGroupClassName}
                >
                  {radioOptions.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Aucune option disponible</p>
                  ) : (
                    radioOptions.map((option) => (
                      <div key={option.value} className="flex items-center space-x-2">
                        <RadioGroupItem value={option.value} id={`${field.slug}-${option.value}`} />
                        <Label 
                          htmlFor={`${field.slug}-${option.value}`} 
                          className={`font-normal cursor-pointer ${getLabelClassName(field.label_style)}`}
                          style={getLabelInlineStyle(field.label_style)}
                        >
                          {option.label}
                        </Label>
                      </div>
                    ))
                  )}
                </RadioGroup>
              );
            }}
          />
        );

      case "date": {
        const isStartDate = field.slug === "date_de_demarrage";
        const todayIso = isStartDate
          ? new Date().toISOString().slice(0, 10)
          : undefined;
        return (
          <Controller
            name={field.slug}
            control={control}
            defaultValue={field.default_value ?? ""}
            rules={{
              required: field.required,
              ...(isStartDate && {
                validate: (v: unknown) =>
                  !v ||
                  typeof v !== "string" ||
                  v >= todayIso! ||
                  "La date de démarrage ne peut pas être dans le passé.",
              }),
            }}
            render={({ field: { onChange, value } }) => (
              <Input
                {...commonProps}
                type="date"
                min={todayIso}
                value={value as string | undefined}
                onChange={onChange}
                onBlur={() => evaluateCrossFieldWarnings(field.slug)}
              />
            )}
          />
        );
      }

      default:
        return (
          <Controller
            name={field.slug}
            control={control}
            defaultValue={field.default_value ?? ""}
            rules={{ required: field.required }}
            render={({ field: { onChange, value } }) => (
              <Input
                {...commonProps}
                type="text"
                value={value as string | undefined}
                onChange={onChange}
                onBlur={() => evaluateCrossFieldWarnings(field.slug)}
              />
            )}
          />
        );
    }
  };

  if (loadingSimulator) {
    return (
      <div className="flex flex-col h-full">
        <Header />
        <div className="flex-1 p-6">
          <Skeleton className="h-[600px] w-full" />
        </div>
      </div>
    );
  }

  // Block non-admin access to preview mode
  if (isPreviewMode && !isAdmin) {
    return (
      <div className="flex flex-col h-full">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">Accès restreint</h3>
              <p className="text-muted-foreground mb-4">
                Le mode aperçu est réservé aux administrateurs.
              </p>
              <Button asChild>
                <Link to="/simulators">Retour aux simulateurs</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!simulator) {
    return (
      <div className="flex flex-col h-full">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <p>Simulateur non trouvé</p>
        </div>
      </div>
    );
  }

  if (availableVersions.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <XCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">
                Aucune version {isPreviewMode ? "disponible" : "publiée"}
              </h3>
              <p className="text-muted-foreground mb-4">
                Ce simulateur n&apos;a pas de version {isPreviewMode ? "disponible" : "publiée"}.
              </p>
              <Button asChild>
                {isAdmin ? (
                  <Link to="/admin/simulators/$id" params={{ id: String(simulatorId) }}>
                    Configurer le simulateur
                  </Link>
                ) : (
                  <Link to="/simulators">Retour aux simulateurs</Link>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <Header />

      <div className="flex-1 p-6 overflow-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" size="icon" asChild>
              {isPreviewMode ? (
                <Link to="/admin/simulators/$id" params={{ id: String(simulatorId) }}>
                  <ArrowLeft className="h-4 w-4" />
                </Link>
              ) : (
                <Link to="/simulators">
                  <ArrowLeft className="h-4 w-4" />
                </Link>
              )}
            </Button>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold">{simulator.name}</h1>
                {isPreviewMode && (
                  <Badge variant="outline" className="gap-1">
                    <Eye className="h-3 w-3" />
                    Aperçu
                  </Badge>
                )}
              </div>
              <p className="text-muted-foreground">{simulator.description}</p>
            </div>
            
            {/* Resources and Glossary Buttons */}
            <div className="flex items-center gap-2">
              {enableResources && resources.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setResourcesOpen(true)}
                  className="gap-2"
                >
                  <Library className="h-4 w-4" />
                  {resourcesTitle}
                </Button>
              )}
              {enableGlossary && glossaryTerms.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setGlossaryOpen(true)}
                  className="gap-2"
                >
                  <Book className="h-4 w-4" />
                  {glossaryTitle}
                </Button>
              )}
            </div>
          </div>

          {selectedVersion && (
            <div className="flex items-center gap-2">
              <Label className="text-muted-foreground">Version:</Label>
              <div className="px-3 py-1.5 bg-muted rounded-md text-sm">
                <span className="font-medium">Version {selectedVersion.version_number}</span>
                {selectedVersion.name && (
                  <span className="text-primary"> - {selectedVersion.name}</span>
                )}
                {selectedVersion.status !== "published" && (
                  <span className="text-muted-foreground"> ({selectedVersion.status === "draft" ? "Brouillon" : "Archivée"})</span>
                )}
                {selectedVersion.published_at && (
                  <span className="text-muted-foreground"> • {formatDate(selectedVersion.published_at)}</span>
                )}
              </div>
            </div>
          )}
        </motion.div>
        
        {/* Foreword Section */}
        {enableForeword && forewordContent && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Card className="border-primary/20 bg-primary/5">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <BookOpen className="h-5 w-5 text-primary" />
                  {forewordTitle}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {forewordContent}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        )}

        <div className={getMainGridClass()}>
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className={getFormSectionClass()}
          >
            <Card>
              <CardHeader className="space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle>Paramètres</CardTitle>
                    <CardDescription>
                      Renseignez les valeurs pour établir la cotation
                    </CardDescription>
                  </div>
                  {/* Navigation dropdown and draft controls */}
                  <div className="flex items-center gap-2">
                    {/* Draft indicator */}
                    {!autoCalculate && autoSaveDraft && (
                      <div className="flex items-center gap-2">
                        {draftSavedAt ? (
                          <Badge variant="secondary" className="gap-1 text-xs">
                            <Save className="h-3 w-3" />
                            {hasUnsavedChanges ? (
                              <span className="flex items-center gap-1">
                                Modifications non sauvegardées
                                <span className="h-1.5 w-1.5 rounded-full bg-warning animate-pulse" />
                              </span>
                            ) : (
                              <span>Brouillon</span>
                            )}
                          </Badge>
                        ) : isDraftLoaded && Object.values(watchedValues).some(v => v !== undefined && v !== null && v !== "") ? (
                          <Badge variant="outline" className="gap-1 text-xs text-muted-foreground">
                            <Save className="h-3 w-3" />
                            Non sauvegardé
                          </Badge>
                        ) : null}
                        {/* Manual save button */}
                        {hasUnsavedChanges && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleSaveDraftManually}
                            disabled={isSavingDraft}
                            className="h-7 px-2"
                          >
                            {isSavingDraft ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Save className="h-3 w-3" />
                            )}
                          </Button>
                        )}
                      </div>
                    )}
                    
                    {/* Navigation dropdown */}
                    {groupNavigation.length > 1 && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className="gap-2">
                            <List className="h-4 w-4" />
                            <span className="hidden sm:inline">Sections</span>
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-64 p-2">
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-muted-foreground px-2 py-1">
                              Aller à la section
                            </p>
                            {groupNavigation.map((group) => (
                              <button
                                key={group.slug}
                                type="button"
                                onClick={() => handleNavClick(group.slug)}
                                className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded-md hover:bg-muted transition-colors text-left"
                              >
                                {group.status === 'complete' ? (
                                  <CheckCircle2 className="h-4 w-4 text-success flex-shrink-0" />
                                ) : group.status === 'error' ? (
                                  <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                                ) : (
                                  <Circle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                )}
                                <span className="truncate">{group.name}</span>
                              </button>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}

                    {vroomImport?.disponible && (
                      <>
                        <input
                          ref={vroomInputRef}
                          type="file"
                          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                          className="hidden"
                          onChange={(e) => handleVroomFile(e.target)}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => vroomInputRef.current?.click()}
                          disabled={vroomImportMutation.isPending}
                          className="gap-2"
                          title={`Pré-remplir jusqu'à ${vroomImport.nb_circuits_max} circuits depuis un export VROOM d'OPTIMISS`}
                        >
                          {vroomImportMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <FileSpreadsheet className="h-4 w-4" />
                          )}
                          <span className="hidden sm:inline">Importer VROOM</span>
                        </Button>
                      </>
                    )}

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleReset}
                      className="gap-2"
                    >
                      <RotateCcw className="h-4 w-4" />
                      <span className="hidden sm:inline">Réinitialiser</span>
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingFields ? (
                  <div className="space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : (
                  <form 
                      noValidate
                      onSubmit={handleSubmit(onSubmit, onInvalid)} 
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
                          e.preventDefault();
                        }
                      }}
                      className="space-y-6"
                    >
                    <div className={getFormGroupsGridClass()}>
                      {formGroups?.filter(isGroupVisible).map((group) => (
                        <div key={group.id} id={`group-${group.slug}`} className={getGroupWrapperClass(group)}>
                          <FormGroupRenderer
                            group={group}
                            isFieldVisible={isFieldVisible}
                            isGroupVisible={isGroupVisible}
                            renderField={renderField}
                            errors={errors}
                            touchedFields={touchedFields}
                            isSubmitted={isSubmitted}
                            depth={0}
                            watchedValues={watchedValues}
                            onToggleGroup={handleToggleGroup}
                            fieldWarnings={fieldWarnings}
                            crossFieldWarnings={crossFieldWarnings}
                            crossFieldWarningStyles={crossFieldWarningStyles}
                            crossFieldErrors={crossFieldErrors}
                            groupValidationErrors={groupValidationErrors}
                            fieldValidationErrors={fieldValidationErrors}
                          />
                        </div>
                      ))}
                    </div>

                    <div className="border-t pt-4 space-y-3">
                      {/* Notes field - collapsible */}
                      {enableNotes && (
                        <Collapsible>
                          <CollapsibleTrigger asChild>
                            <button
                              type="button"
                              className="flex items-center gap-2 w-full text-left py-2 hover:text-primary transition-colors group"
                            >
                              <ChevronRight className="h-4 w-4 transition-transform group-data-[state=open]:rotate-90" />
                              <span className="text-sm font-medium">Notes</span>
                              <span className="text-xs text-muted-foreground">(optionnel)</span>
                            </button>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="pl-6 pb-2">
                              <Textarea
                                id="notes"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Ajouter des notes..."
                                rows={2}
                              />
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      )}
                      
                      {/* Attachments - collapsible, always in form */}
                      {enableAttachments && selectedVersionId && (
                        <Collapsible defaultOpen={false}>
                          <CollapsibleTrigger asChild>
                            <button
                              type="button"
                              className="flex items-center gap-2 w-full text-left py-2 hover:text-primary transition-colors group"
                            >
                              <ChevronRight className="h-4 w-4 transition-transform group-data-[state=open]:rotate-90" />
                              <span className="text-sm font-medium">Pièces jointes</span>
                              <span className="text-xs text-muted-foreground">(optionnel)</span>
                              {tempAttachments.length > 0 && (
                                <Badge variant="secondary" className="ml-auto text-xs">
                                  {tempAttachments.length} fichier{tempAttachments.length > 1 ? "s" : ""}
                                </Badge>
                              )}
                            </button>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="pl-6 pb-2">
                              <AttachmentsSection
                                versionId={selectedVersionId}
                                tempAttachments={tempAttachments}
                                onTempAttachmentsChange={setTempAttachments}
                                maxAttachments={maxAttachments}
                                allowedFileTypes={allowedFileTypes}
                                compact
                                hideHeader
                              />
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      )}
                    </div>

                    <div className="flex gap-3">
                      {autoCalculate ? (
                        // Auto-calculate mode: show status indicator instead of button
                        <div className="flex-1 flex items-center justify-center gap-2 p-2 rounded-md bg-muted/50 text-sm text-muted-foreground">
                          {executeMutation.isPending ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span>Calcul automatique en cours...</span>
                            </>
                          ) : (
                            <>
                              <Zap className="h-4 w-4 text-success" />
                              <span>Calcul automatique activé</span>
                            </>
                          )}
                        </div>
                      ) : (
                        // Manual mode: show calculate button
                        <Button
                          type="submit"
                          className="flex-1 relative"
                          disabled={executeMutation.isPending}
                        >
                          {executeMutation.isPending ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Calcul en cours...
                            </>
                          ) : (
                            <>
                              <Play className="mr-2 h-4 w-4" />
                              Calculer
                            </>
                          )}
                          {/* Error count badge */}
                          {Object.keys(errors).length > 0 && isSubmitted && !executeMutation.isPending && (
                            <Badge 
                              variant="destructive" 
                              className="absolute -top-2 -right-2 h-5 min-w-5 px-1 text-xs flex items-center justify-center"
                            >
                              {Object.keys(errors).length}
                            </Badge>
                          )}
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleReset}
                      >
                        <RotateCcw className="mr-2 h-4 w-4" />
                        Réinitialiser
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Results */}
          <motion.div
            id="results-section"
            initial={{ opacity: 0, x: resultsPosition === "right" ? 20 : 0, y: resultsPosition === "bottom" ? 20 : 0 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ delay: 0.2 }}
            className={getResultsSectionClass()}
          >
            <Card className={resultsPosition === "right" ? "sticky top-6" : ""}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="h-5 w-5" />
                    Résultats
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {resultatPerime && !autoCalculate && (
                  <div className="mb-4 p-3 rounded-lg bg-warning/10 border border-warning/30 flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
                    <p className="text-sm text-warning-dark">
                      Les saisies ont changé depuis ce calcul : les montants et les lignes
                      affichés sont ceux du calcul précédent. Relancez le calcul pour les
                      mettre à jour.
                    </p>
                  </div>
                )}
                <AnimatePresence mode="wait">
                  {!result ? (
                    <motion.div
                      key="empty"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="text-center py-12 text-muted-foreground"
                    >
                      {autoCalculate ? (
                        <>
                          <Zap className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p>Les résultats s&apos;afficheront automatiquement</p>
                          <p className="text-sm mt-1">Remplissez le formulaire pour lancer le calcul</p>
                        </>
                      ) : (
                        <>
                          <Calculator className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p>Remplissez le formulaire et cliquez sur Calculer</p>
                        </>
                      )}
                    </motion.div>
                  ) : !result.success ? (
                    <motion.div
                      key="error"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      className="space-y-4"
                    >
                      <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/30">
                        <div className="flex items-center gap-2 mb-2">
                          <XCircle className="h-5 w-5 text-destructive" />
                          <span className="font-medium text-destructive">
                            Erreur de calcul
                          </span>
                        </div>
                        <ul className="list-disc list-inside text-sm text-destructive">
                          {result.errors?.map((error, i) => (
                            <li key={i}>{error}</li>
                          ))}
                        </ul>
                      </div>
                      
                      {/* Detailed error information */}
                      {result.error_details && result.error_details.length > 0 && (
                        <div className="p-4 rounded-lg bg-warning/10 border border-warning/30 space-y-3">
                          <p className="font-medium text-warning text-sm">
                            Détails du débogage :
                          </p>
                          {result.error_details.map((detail, i) => (
                            <div key={i} className="space-y-2 text-xs bg-warning/10 p-3 rounded">
                              {detail.field_slug && (
                                <p>
                                  <span className="font-medium">Champ : </span>
                                  <code className="bg-muted px-1 rounded">{detail.field_slug}</code>
                                </p>
                              )}
                              {detail.error_type && (
                                <p>
                                  <span className="font-medium">Type d&apos;erreur : </span>
                                  <code className="bg-muted px-1 rounded">{detail.error_type}</code>
                                </p>
                              )}
                              {detail.formula && (
                                <details className="cursor-pointer">
                                  <summary className="font-medium text-warning-dark hover:text-warning-dark">
                                    Voir la formule
                                  </summary>
                                  <pre className="mt-1 p-2 bg-muted/50 rounded text-[10px] whitespace-pre-wrap overflow-auto max-h-32">
                                    {detail.formula}
                                  </pre>
                                </details>
                              )}
                              {detail.null_variables && detail.null_variables.length > 0 && (
                                <p className="text-error">
                                  <span className="font-medium">⚠️ Variables manquantes/nulles : </span>
                                  {detail.null_variables.map((v, j) => (
                                    <code key={j} className="bg-error/20 px-1 rounded mx-0.5">{v}</code>
                                  ))}
                                </p>
                              )}
                              {detail.available_values && Object.keys(detail.available_values).length > 0 && (
                                <details className="cursor-pointer">
                                  <summary className="font-medium text-warning-dark hover:text-warning-dark">
                                    Voir les valeurs disponibles ({Object.keys(detail.available_values).length})
                                  </summary>
                                  <div className="mt-1 max-h-40 overflow-auto bg-muted/50 p-2 rounded">
                                    <pre className="text-[10px] whitespace-pre-wrap">
                                      {JSON.stringify(detail.available_values, null, 2)}
                                    </pre>
                                  </div>
                                </details>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  ) : (
                    <motion.div
                      key="results"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      className="space-y-6"
                    >
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-success" />
                          Calcul réussi
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {result.execution_time_ms}ms
                        </div>
                      </div>

                      {/* Toggle button to show/hide zero values */}
                      {hideZeroOutputs && showAllOutputsButton && (
                        <div className="flex justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowAllOutputs(!showAllOutputs)}
                            className="gap-2"
                          >
                            {showAllOutputs ? (
                              <>
                                <EyeOff className="h-4 w-4" />
                                Masquer les champs filtrés
                              </>
                            ) : (
                              <>
                                <Eye className="h-4 w-4" />
                                Afficher tout
                              </>
                            )}
                          </Button>
                        </div>
                      )}

                      {/* Section for "never visible" fields (shown first when "show all" is active) */}
                      {showAllOutputs && (() => {
                        // Collect fields with visibility_mode === "never" across all groups
                        // Don't show conditional fields whose condition is not met
                        const hiddenFieldsGroups = (groupedOutputs || []).map(
                          ({ name: groupName, fields, backgroundColor: rawBackgroundColor }) => {
                            // Only show fields explicitly set to "never" visible
                            // Conditional fields with unmet conditions should NOT appear here
                            let hiddenFields = fields.filter(field => field.visibility_mode === "never");
                            
                            // Apply zero value filtering if enabled
                            if (hideZeroOutputs) {
                              hiddenFields = hiddenFields.filter(field => !isZeroValue(result.outputs?.[field.slug]));
                            }
                            
                            return { groupName, fields: hiddenFields, backgroundColor: rawBackgroundColor };
                          }
                        ).filter(group => group.fields.length > 0);
                        
                        if (hiddenFieldsGroups.length === 0) return null;
                        
                        return (
                          <div className="space-y-4">
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <EyeOff className="h-4 w-4" />
                              <h3 className="text-sm font-medium">Champs jamais affichés</h3>
                            </div>
                            <div className={`${getOutputGroupsGridClass()} p-4 rounded-lg border-2 border-dashed border-muted-foreground/30 bg-muted/20`}>
                              {hiddenFieldsGroups.map(({ groupName, fields, backgroundColor: rawBackgroundColor }) => {
                                const backgroundColor = isDarkMode ? undefined : rawBackgroundColor;
                                
                                return (
                                  <div 
                                    key={`hidden-${groupName}`}
                                    className={backgroundColor ? "p-3 rounded-lg" : ""}
                                    style={backgroundColor ? { backgroundColor } : undefined}
                                  >
                                    <h4 className="text-sm font-medium text-muted-foreground mb-3">
                                      {groupName}
                                    </h4>
                                    <div className="space-y-3">
                                      {fields
                                        .sort((a, b) => a.order - b.order)
                                        .map((field) => (
                                          <div
                                            key={field.id}
                                            className="flex items-center justify-between p-3 rounded-lg bg-muted/30"
                                          >
                                            <div className="opacity-80">
                                              <p className="font-medium">{parseBBCode(field.label)}</p>
                                              {showOutputDescriptions && field.description && (
                                                <p className="text-xs text-muted-foreground">
                                                  {field.description}
                                                </p>
                                              )}
                                            </div>
                                            <div className="text-right">
                                              <p className="text-xl font-bold text-primary/80">
                                                {formatOutput(field, result.outputs?.[field.slug])}
                                              </p>
                                            </div>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Section for always visible fields */}
                      {(() => {
                        const visibleFieldsGroups = (groupedOutputs || []).map(
                          ({ name: groupName, fields, backgroundColor: rawBackgroundColor }) => {
                            // Fields that are normally visible
                            let visibleFields = fields.filter(field => isOutputFieldVisible(field));
                            
                            // Apply zero value filtering if enabled
                            if (hideZeroOutputs) {
                              visibleFields = visibleFields.filter(field => !isZeroValue(result.outputs?.[field.slug]));
                            }
                            
                            return { groupName, fields: visibleFields, backgroundColor: rawBackgroundColor };
                          }
                        ).filter(group => group.fields.length > 0);
                        
                        if (visibleFieldsGroups.length === 0) return null;
                        
                        return (
                          <div className="space-y-4">
                            {showAllOutputs && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Eye className="h-4 w-4" />
                                <h3 className="text-sm font-medium">Champs toujours affichés</h3>
                              </div>
                            )}
                            <div className={getOutputGroupsGridClass()}>
                              {visibleFieldsGroups.map(({ groupName, fields, backgroundColor: rawBackgroundColor }) => {
                                const backgroundColor = isDarkMode ? undefined : rawBackgroundColor;
                                
                                return (
                                  <div 
                                    key={groupName}
                                    className={backgroundColor ? "p-3 rounded-lg" : ""}
                                    style={backgroundColor ? { backgroundColor } : undefined}
                                  >
                                    <h4 className="text-sm font-medium text-muted-foreground mb-3">
                                      {groupName}
                                    </h4>
                                    <div className="space-y-3">
                                      {fields
                                        .sort((a, b) => a.order - b.order)
                                        .map((field) => (
                                          <div
                                            key={field.id}
                                            className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                                          >
                                            <div>
                                              <p className="font-medium">{parseBBCode(field.label)}</p>
                                              {showOutputDescriptions && field.description && (
                                                <p className="text-xs text-muted-foreground">
                                                  {field.description}
                                                </p>
                                              )}
                                            </div>
                                            <div className="text-right">
                                              <p className="text-xl font-bold text-primary">
                                                {formatOutput(field, result.outputs?.[field.slug])}
                                              </p>
                                            </div>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}

                      {result.warnings && result.warnings.length > 0 && (
                        <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                          <p className="text-sm font-medium text-yellow-500 mb-1">
                            Avertissements
                          </p>
                          <ul className="text-sm text-yellow-400 list-disc list-inside">
                            {result.warnings.map((warning, i) => (
                              <li key={i}>{warning}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {result.simulation_id ? (
                        <div className="space-y-3 pt-2 border-t">
                          <p className="text-xs text-muted-foreground text-center font-mono">
                            {result.simulation_ref || `Cotation #${result.simulation_id}`} sauvegardée
                          </p>
                          {!isViewer && (
                            <CreateProposalButton
                              simulationId={result.simulation_id}
                              versionId={selectedVersionId!}
                              simulatorName={simulator?.name || "Cotation"}
                              simulationInputs={calculatedInputs || {}}
                              simulationOutputs={result.outputs || {}}
                            />
                          )}
                        </div>
                      ) : !simulationEventSaved ? (
                        <div className="pt-4 border-t">
                          <Button
                            onClick={() => saveEventMutation.mutate()}
                            className="w-full"
                            disabled={saveEventMutation.isPending || !result.success}
                          >
                            {saveEventMutation.isPending ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Enregistrement...
                              </>
                            ) : (
                              <>
                                <Save className="mr-2 h-4 w-4" />
                                Enregistrer la simulation
                              </>
                            )}
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3 pt-4 border-t">
                          <div className="flex items-center justify-center gap-2 text-sm text-success">
                            <CheckCircle2 className="h-4 w-4" />
                            <span>Simulation enregistrée dans votre historique</span>
                          </div>
                          {!isViewer && (
                            <Button
                              onClick={handleOpenSaveDialog}
                              className="w-full"
                              disabled={saveMutation.isPending}
                            >
                              {saveMutation.isPending ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Enregistrement...
                                </>
                              ) : (
                                <>
                                  <Save className="mr-2 h-4 w-4" />
                                  Enregistrer la cotation
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      )}
                      
                    </motion.div>
                  )}
                </AnimatePresence>
              </CardContent>
            </Card>
          </motion.div>
        </div>
        
        
      </div>
      
      {/* Resources Modal */}
      <ResourcesModal
        open={resourcesOpen}
        onOpenChange={setResourcesOpen}
        title={resourcesTitle}
        resources={resources}
      />
      
      {/* Glossary Modal */}
      <GlossaryModal
        open={glossaryOpen}
        onOpenChange={setGlossaryOpen}
        title={glossaryTitle}
        terms={glossaryTerms}
      />

      {/* Récapitulatif d'un import VROOM, à accepter avant de remplir le formulaire */}
      <Dialog open={vroomPreview !== null} onOpenChange={(open) => { if (!open) setVroomPreview(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Import VROOM
            </DialogTitle>
            <DialogDescription>
              {`${vroomPreview?.fileName ?? ""} — vérifiez ce qui sera repris avant de l'appliquer au formulaire.`}
            </DialogDescription>
          </DialogHeader>

          {vroomPreview && vroomPreviewSummary && (
            <div className="space-y-5 text-sm">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  ["Circuits", String(vroomPreview.result.circuits.length)],
                  ["Points de livraison", String(vroomPreviewSummary.nbPoints)],
                  ["Km par jour", formatNumber(vroomPreviewSummary.totalKm, 2)],
                  ["Valeurs importées", String(vroomPreviewSummary.nbValues)],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-lg border bg-muted/40 p-3">
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className="text-lg font-semibold">{value}</p>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <p className="font-medium">Champs remplis pour chaque circuit</p>
                <ul className="list-disc pl-5 space-y-0.5 text-muted-foreground">
                  {vroomPreviewSummary.fieldLabels.map((label) => (
                    <li key={label}>{label}</li>
                  ))}
                </ul>
                {vroomPreview.result.precisions.map((precision) => (
                  <p key={precision} className="text-xs text-muted-foreground">{precision}</p>
                ))}
              </div>

              <div className="max-h-56 overflow-y-auto rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Circuit</TableHead>
                      <TableHead className="text-right">Points</TableHead>
                      <TableHead className="text-right">Km / jour</TableHead>
                      <TableHead className="text-right">Temps importé</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vroomPreview.result.circuits.map((circuit) => (
                      <TableRow key={circuit.numero}>
                        <TableCell>{`${circuit.numero}. ${circuit.nom}`}</TableCell>
                        <TableCell className="text-right">{circuit.nb_points}</TableCell>
                        <TableCell className="text-right">{formatNumber(circuit.distance_km, 2)}</TableCell>
                        <TableCell className="text-right">{formatDuration(circuit.duree_min)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="space-y-2">
                <p className="font-medium">{"À saisir après l'import"}</p>
                <p className="text-muted-foreground">
                  {vroomPreviewSummary.nbMissingRequired > 1
                    ? `${vroomPreviewSummary.nbMissingRequired} champs obligatoires resteront vides et apparaîtront en rouge dans le formulaire.`
                    : vroomPreviewSummary.nbMissingRequired === 1
                      ? "1 champ obligatoire restera vide et apparaîtra en rouge dans le formulaire."
                      : "Aucun champ obligatoire ne restera vide."}
                </p>
                <ul className="space-y-2">
                  {vroomPreview.result.a_completer.map((field) => (
                    <li key={field.libelle}>
                      <span className="font-medium">{field.libelle}</span>
                      <span className="text-muted-foreground">{` — ${field.explication}`}</span>
                    </li>
                  ))}
                </ul>
                {vroomPreviewSummary.models.length === 1 && (
                  <p className="text-xs text-muted-foreground">
                    {`Véhicule retenu par OPTIMISS : ${vroomPreviewSummary.models[0]}.`}
                  </p>
                )}
              </div>

              {vroomPreview.result.avertissements.length > 0 && (
                <div className="p-3 rounded-lg bg-warning/10 border border-warning/30 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
                  <ul className="space-y-1 text-warning-dark">
                    {vroomPreview.result.avertissements.map((message) => (
                      <li key={message}>{message}</li>
                    ))}
                  </ul>
                </div>
              )}

              {vroomPreview.replacesInput && (
                <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive">
                  {"L'import remplacera la saisie en cours, brouillon compris."}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setVroomPreview(null)}>
              Annuler
            </Button>
            <Button type="button" onClick={acceptVroomImport}>
              Importer dans le formulaire
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Cotation Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent ref={saveDialogRef} className={saveGroups.length > 0 ? "sm:max-w-2xl" : "sm:max-w-md"}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Save className="h-5 w-5" />
              Enregistrer la cotation
            </DialogTitle>
            <DialogDescription>
              {saveGroups.length > 0
                ? "Complétez les informations ci-dessous puis enregistrez votre cotation."
                : "Donnez un nom à votre cotation pour la retrouver facilement dans votre historique."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            {saveGroups.length > 0 && (
              <div className="space-y-4">
                {saveGroups.filter(isGroupVisible).map((group) => (
                  <div key={group.id}>
                    <FormGroupRenderer
                      group={group}
                      isFieldVisible={isFieldVisible}
                      isGroupVisible={isGroupVisible}
                      renderField={renderField}
                      errors={errors}
                      touchedFields={touchedFields}
                      isSubmitted={false}
                      depth={0}
                      watchedValues={watchedValues}
                      onToggleGroup={handleToggleGroup}
                      fieldWarnings={fieldWarnings}
                      crossFieldWarnings={crossFieldWarnings}
                      crossFieldWarningStyles={crossFieldWarningStyles}
                      crossFieldErrors={crossFieldErrors}
                      groupValidationErrors={{}}
                      fieldValidationErrors={{}}
                    />
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-2">
              <Label>Agence(s)</Label>
              {userAgencies.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Aucune agence disponible pour vos groupes.
                </p>
              ) : (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-between font-normal"
                    >
                      {selectedAgencyIds.length === 0
                        ? "Sélectionner une ou plusieurs agences..."
                        : `${selectedAgencyIds.length} agence${selectedAgencyIds.length > 1 ? "s" : ""} sélectionnée${selectedAgencyIds.length > 1 ? "s" : ""}`}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start" container={saveDialogRef.current}>
                    <Command>
                      <CommandInput placeholder="Rechercher une agence..." />
                      <CommandList>
                        <CommandEmpty>Aucune agence trouvée.</CommandEmpty>
                        {(() => {
                          const grouped = new Map<string, typeof userAgencies>();
                          for (const agency of userAgencies) {
                            const key = agency.group_name || "Autre";
                            if (!grouped.has(key)) grouped.set(key, []);
                            grouped.get(key)!.push(agency);
                          }
                          return Array.from(grouped.entries()).map(([groupName, agencies]) => (
                            <CommandGroup key={groupName} heading={groupName}>
                              {agencies.map((agency) => {
                                const isSelected = selectedAgencyIds.includes(agency.id);
                                return (
                                  <CommandItem
                                    key={agency.id}
                                    value={agency.name}
                                    onSelect={() => {
                                      setSelectedAgencyIds((prev) =>
                                        isSelected
                                          ? prev.filter((id) => id !== agency.id)
                                          : [...prev, agency.id]
                                      );
                                    }}
                                  >
                                    <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${isSelected ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground"}`}>
                                      {isSelected && <CheckCircle2 className="h-3 w-3" />}
                                    </div>
                                    {agency.name}
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          ));
                        })()}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
              {selectedAgencyIds.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {selectedAgencyIds.map((id) => {
                    const agency = userAgencies.find((a) => a.id === id);
                    return agency ? (
                      <Badge
                        key={id}
                        variant="secondary"
                        className="cursor-pointer"
                        onClick={() =>
                          setSelectedAgencyIds((prev) =>
                            prev.filter((aid) => aid !== id)
                          )
                        }
                      >
                        {agency.name} ×
                      </Badge>
                    ) : null;
                  })}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="simulation-name">Nom de la cotation</Label>
              <Input
                id="simulation-name"
                placeholder={generateDefaultSimulationName()}
                value={simulationName}
                onChange={(e) => {
                  hasManuallyEditedName.current = true;
                  setSimulationName(e.target.value);
                }}
              />
              <p className="text-xs text-muted-foreground">
                Laissez vide pour utiliser le nom par défaut
              </p>
            </div>
            {enableNotes && (
              <div className="space-y-2">
                <Label htmlFor="simulation-notes">Notes (optionnel)</Label>
                <Textarea
                  id="simulation-notes"
                  placeholder="Ajoutez des notes sur cette cotation..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowSaveDialog(false)}
              disabled={saveMutation.isPending}
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={handleSaveSimulation}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Enregistrer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Helper function to evaluate selection conditions (same logic as backend)
function evaluateSelectionConditions(
  conditions: { logic?: string; conditions?: Array<{ field_slug: string; operator: string; value?: unknown }> } | null,
  inputs: Record<string, unknown>,
  outputs: Record<string, unknown>
): boolean {
  if (!conditions || !conditions.conditions || conditions.conditions.length === 0) {
    return true; // No conditions = always matches
  }
  
  const logic = conditions.logic || "AND";
  const results: boolean[] = [];
  
  for (const condition of conditions.conditions) {
    const fieldSlug = condition.field_slug;
    const targetValue = condition.value;
    const operator = condition.operator || "equals";
    
    // Check both inputs and outputs - inputs take priority
    let fieldValue = inputs[fieldSlug];
    if (fieldValue === undefined) {
      fieldValue = outputs[fieldSlug];
    }
    
    let result = false;
    
    // Handle operators
    if (fieldValue === null || fieldValue === undefined) {
      if (operator === "is_empty") result = true;
      else if (operator === "is_not_empty") result = false;
      else result = false;
    } else {
      switch (operator) {
        case "equals":
          result = String(fieldValue) === String(targetValue);
          break;
        case "not_equals":
          result = String(fieldValue) !== String(targetValue);
          break;
        case "greater_than":
          result = Number(fieldValue) > Number(targetValue);
          break;
        case "greater_than_or_equals":
          result = Number(fieldValue) >= Number(targetValue);
          break;
        case "less_than":
          result = Number(fieldValue) < Number(targetValue);
          break;
        case "less_than_or_equals":
          result = Number(fieldValue) <= Number(targetValue);
          break;
        case "contains":
          result = String(fieldValue).toLowerCase().includes(String(targetValue).toLowerCase());
          break;
        case "not_contains":
          result = !String(fieldValue).toLowerCase().includes(String(targetValue).toLowerCase());
          break;
        case "is_empty":
          result = fieldValue === "" || fieldValue === null || fieldValue === undefined;
          break;
        case "is_not_empty":
          result = fieldValue !== "" && fieldValue !== null && fieldValue !== undefined;
          break;
        case "in":
          const inValues = String(targetValue).split(",").map(v => v.trim());
          result = inValues.includes(String(fieldValue));
          break;
        case "not_in":
          const notInValues = String(targetValue).split(",").map(v => v.trim());
          result = !notInValues.includes(String(fieldValue));
          break;
        default:
          result = true;
      }
    }
    
    results.push(result);
  }
  
  if (results.length === 0) return true;
  return logic === "AND" ? results.every(r => r) : results.some(r => r);
}

// Component to create a pricing proposal from a simulation
function CreateProposalButton({
  simulationId,
  versionId,
  simulatorName,
  simulationInputs,
  simulationOutputs,
}: {
  simulationId: number;
  versionId: number;
  simulatorName: string;
  simulationInputs: Record<string, unknown>;
  simulationOutputs: Record<string, unknown>;
}) {
  const [isCreating, setIsCreating] = useState(false);
  const [proposalName, setProposalName] = useState(`Proposition - ${simulatorName}`);
  const [showDialog, setShowDialog] = useState(false);

  // Fetch available templates for the version
  const { data: templatesData } = useQuery({
    queryKey: ["proposal-templates", versionId, { active_only: true }],
    queryFn: () => listProposalTemplates(versionId, { active_only: true }),
    enabled: showDialog && !!versionId,
  });

  // Determine which template would be auto-selected
  const autoSelectedTemplate = useMemo(() => {
    if (!templatesData || templatesData.items.length === 0) return null;
    
    // Sort by priority (descending)
    const sortedTemplates = [...templatesData.items].sort((a, b) => (b.priority || 0) - (a.priority || 0));
    
    // First pass: find templates with conditions that match
    for (const template of sortedTemplates) {
      if (template.selection_conditions?.conditions?.length) {
        if (evaluateSelectionConditions(template.selection_conditions, simulationInputs, simulationOutputs)) {
          return template;
        }
      }
    }
    
    // Second pass: find templates without conditions (fallback)
    for (const template of sortedTemplates) {
      if (!template.selection_conditions?.conditions?.length) {
        return template;
      }
    }
    
    return null;
  }, [templatesData, simulationInputs, simulationOutputs]);

  const handleCreate = async () => {
    setIsCreating(true);
    try {
      const templateId = autoSelectedTemplate?.id;
      const proposal = await createProposalFromSimulation(simulationId, proposalName, templateId);

      toast.info("Génération du PPTX en cours...");
      const pptxBlob = await downloadProposalPptx(proposal.id);

      const sanitizeForFileName = (value: string) =>
        value
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-zA-Z0-9]+/g, "-")
          .replace(/(^-|-$)+/g, "");

      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, "0");
      const dateTime = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}h${pad(now.getMinutes())}`;
      const rawRaisonSociale = (simulationInputs["raison_sociale"] as string | undefined)?.trim();
      const fileNameParts = [
        "PROPAL",
        sanitizeForFileName(simulatorName) || "Simulateur",
        sanitizeForFileName(rawRaisonSociale || "") || "Sans-RS",
        dateTime,
      ];
      const fileName = `${fileNameParts.join("_")}.pptx`;

      const url = window.URL.createObjectURL(pptxBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success("Proposition créée et téléchargée avec succès");
    } catch (error) {
      toast.error("Erreur lors de la création", {
        description: getErrorMessage(error),
      });
    } finally {
      setIsCreating(false);
      setShowDialog(false);
    }
  };

  const handleButtonClick = () => {
    setShowDialog(true);
  };

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <Button
        className="w-full gap-2 h-12 text-base bg-laposte-yellow text-black hover:bg-laposte-yellow-dark"
        onClick={handleButtonClick}
      >
        <FileText className="h-4 w-4" />
        Créer une proposition commerciale
      </Button>

      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Créer une proposition commerciale</DialogTitle>
          <DialogDescription>
            Une proposition commerciale sera créée à partir de cette cotation.
            Le fichier PowerPoint sera téléchargé automatiquement.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div>
            <Label htmlFor="proposalName">Nom de la proposition</Label>
            <Input
              id="proposalName"
              value={proposalName}
              onChange={(e) => setProposalName(e.target.value)}
              placeholder="Nom de la proposition..."
              className="mt-1"
            />
          </div>
          {templatesData && autoSelectedTemplate && (
            <div className="p-3 rounded-md bg-muted text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span>
                  Template : <strong>{autoSelectedTemplate.name}</strong>
                </span>
              </div>
            </div>
          )}
          {templatesData && !autoSelectedTemplate && (
            <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-sm text-destructive">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                <span>
                  Aucun template ne correspond a cette cotation. Veuillez contacter l&apos;administrateur.
                </span>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDialog(false)}
              disabled={isCreating}
            >
              Annuler
            </Button>
            <Button
              onClick={handleCreate}
              disabled={isCreating || !proposalName.trim() || !autoSelectedTemplate}
            >
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Génération en cours...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Télécharger
                </>
              )}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

